/**
 * Knowledge Base Panel - Gestione indici, PDF e web crawling
 * 
 * ARCHITETTURA:
 * - Questo componente fa da INTERFACCIA per i servizi RAG di pigna-2
 * - Tutte le operazioni KB (crawling, indicizzazione, upload PDF) vengono
 *   inoltrate a pigna-2 staging (porta 8002) tramite proxy pigna-studio (porta 8001)
 * - pigna-2 fornisce tutti i servizi RAG sofisticati:
 *   - Web crawling (Playwright/Scrapy)
 *   - Indicizzazione PDF avanzata
 *   - Chunking semantico e testuale
 *   - Embedding multilingua
 *   - OCR avanzato
 *   - Anonimizzazione
 * 
 * FLUSSO:
 * Frontend → pigna-studio (8001) → pigna-2 staging (8002) → FAISS/Qdrant
 * 
 * Vedi: ARCHITETTURA-GENERALE-2026.md per dettagli completi
 */
import { useState, useEffect, useRef } from 'react';
import { API_URL } from '../config';
import { Globe, Trash2, Search, FileText, Loader, BookOpen, ChevronDown, ChevronUp, Settings, Zap, Eye, Database } from 'lucide-react';
import DatabaseConnectorPanel from './DatabaseConnectorPanel';

interface KnowledgeIndex {
  id: string;
  name: string;
  type: 'pdf' | 'web';
  chunks_count: number;
  created_at: string;
  status: 'ready' | 'processing';
}

interface KnowledgeBasePanelProps {
  externalConfig?: any;
  onSectionChange?: (section: 'flows' | 'knowledge' | 'indices' | 'services' | 'help') => void;
}

/**
 * Knowledge Base Panel Component
 * 
 * Questo componente gestisce tutte le operazioni Knowledge Base:
 * - Upload e indicizzazione PDF
 * - Web crawling (Playwright/Scrapy)
 * - Gestione indici FAISS/Qdrant
 * - Preview chunks e preprocessing
 * 
 * ARCHITETTURA:
 * - Tutte le operazioni KB vengono inoltrate a pigna-2 staging (porta 8002)
 *   tramite proxy pigna-studio backend (porta 8001)
 * - pigna-2 fornisce tutti i servizi RAG sofisticati:
 *   - Indicizzazione PDF avanzata (PyMuPDF, pdfplumber, OCR)
 *   - Web crawling (Playwright, Scrapy)
 *   - Chunking semantico e testuale
 *   - Embedding multilingua (BAAI/bge-m3, all-MiniLM-L6-v2, e5-small-v2)
 *   - Ricerca semantica (FAISS, Qdrant)
 *   - OCR avanzato (Tesseract, LLaVA, Claude Vision)
 *   - Anonimizzazione (Presidio)
 * 
 * FLUSSO:
 * Frontend → pigna-studio (8001) → pigna-2 staging (8002) → FAISS/Qdrant
 * 
 * Vedi: ARCHITETTURA-GENERALE-2026.md per dettagli completi
 */
export default function KnowledgeBasePanel({ externalConfig, onSectionChange: _onSectionChange }: KnowledgeBasePanelProps) {
  const [indices, setIndices] = useState<KnowledgeIndex[]>([]);
  const [isUploadingPDF, setIsUploadingPDF] = useState(false);
  const [isCrawling, setIsCrawling] = useState(false);
  const [crawlUrl, setCrawlUrl] = useState('');
  const [webIndexName, setWebIndexName] = useState('');
  // 📊 Stati per Crawling con Streaming
  const [crawlLog, setCrawlLog] = useState<string[]>(['In attesa di avvio crawling...']);
  const [crawlProgress, setCrawlProgress] = useState<{
    pages_processed: number;
    current_url: string | null;
    chunks_created: number;
    phase: string;
  }>({
    pages_processed: 0,
    current_url: null,
    chunks_created: 0,
    phase: 'idle'
  });
  
  // 🐛 Debug dettagliato per crawler (e per pannello Log trasparenza)
  const [crawlDebug, setCrawlDebug] = useState<{
    urls_in_queue: string[];
    urls_visited: string[];
    urls_processed: string[];
    urls_failed: Array<{url: string; error: string}>;
    chunks_by_url: Record<string, number>;
    total_chunks: number;
    errors: string[];
    start_time: number | null;
    last_update: number | null;
    queue_delta?: number; // variazione lunghezza coda (Δ) rispetto all'ultimo aggiornamento
  }>({
    urls_in_queue: [],
    urls_visited: [],
    urls_processed: [],
    urls_failed: [],
    chunks_by_url: {},
    total_chunks: 0,
    errors: [],
    start_time: null,
    last_update: null,
    queue_delta: undefined
  });
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  // 📜 Pannello Log trasparenza algoritmo (sempre apribile col bottone "Log")
  const [showLogPanel, setShowLogPanel] = useState(false);
  // Quando ci sono più crawl (es. background), quale visualizzare nel pannello Log
  const [selectedLogCrawlId, setSelectedLogCrawlId] = useState<string | null>(null);
  const crawlHeartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const backgroundJobsEndpointAvailable = useRef<boolean | null>(null); // null = non ancora provato, false = 404
  const [showConfig, setShowConfig] = useState(false);
  const [showPdfConfig, setShowPdfConfig] = useState(false);
  const [showPdfPreviewSection, setShowPdfPreviewSection] = useState(false);
  const [showWebCrawlerSection, setShowWebCrawlerSection] = useState(false);
  const [showPdfSection, setShowPdfSection] = useState(false);
  const [showAllIndices, setShowAllIndices] = useState(false);
  const [showDatabaseCloudSection, setShowDatabaseCloudSection] = useState(false);
  const [processingIndex, setProcessingIndex] = useState<string | null>(null);
  const [pollingInterval, setPollingInterval] = useState<number | null>(null);
  const [indexingStartTime, setIndexingStartTime] = useState<number | null>(null);
  const [elapsedTime, setElapsedTime] = useState<number>(0);
  
  // 🆕 Stato dettagliato per tracking granulare
  const [detailedStatus, setDetailedStatus] = useState<{
    phase: string;
    total_files: number;
    current_file_index: number;
    current_file_name: string;
    file_chunks_count?: number;
    file_chunks_embedded?: number;
    total_chunks: number;
    message: string;
    config?: {
      embed_model: string;
      extractor: string;
      chunk_size: number;
      chunk_overlap: number;
      use_semantic_chunking: boolean;
      similarity_threshold: number;
      min_chunk_chars: number;
      max_chunk_chars: number;
      use_ocr_fallback: boolean;
      use_presidio: boolean;
    };
  } | null>(null);
  
  // 🔌 Stato connessione backend
  const [backendStatus, setBackendStatus] = useState<'checking' | 'online' | 'offline'>('checking');
  
  // Parametri di configurazione
  const [extractorType, setExtractorType] = useState('pymupdf');
  const [embedModel, setEmbedModel] = useState('BAAI/bge-m3');
  const [chunkSize, setChunkSize] = useState(500);
  const [chunkOverlap, setChunkOverlap] = useState(50);
  const [useSemanticChunking, setUseSemanticChunking] = useState(false); // default OFF: chunking fisso è molto più veloce (70 pagine in secondi vs minuti)
  const [similarityThreshold, setSimilarityThreshold] = useState(0.85);
  const [minChunkChars, setMinChunkChars] = useState(100);
  const [maxChunkChars, setMaxChunkChars] = useState(1500);
  const [enableOCR, setEnableOCR] = useState(false);
  const [usePresidio, setUsePresidio] = useState(false);
  const [enableMultiModal, setEnableMultiModal] = useState(false); // 🆕 Multi-Modal RAG
  
  // 🌐 Opzioni Web Crawler (TUTTE le opzioni dalla sezione 3.B vanilla)
  const [crawlDepth, setCrawlDepth] = useState(3); // default 3, allineato a FastCrawlRequest.max_depth
  const [focusKeywords, setFocusKeywords] = useState('');
  const [domainFilter, setDomainFilter] = useState(''); // 🔒 Dominio da seguire (es. www.marinuzzi.it)
  const [allowExternalSites, setAllowExternalSites] = useState(false); // 🌐 Permetti siti terzi
  const [crawlIndexSuffix, setCrawlIndexSuffix] = useState('');
  const [usePlaywright, setUsePlaywright] = useState(false);
  const [downloadPdfs, setDownloadPdfs] = useState(true);
  const [downloadOnlyPdfs, setDownloadOnlyPdfs] = useState(false);
  const [downloadImages, setDownloadImages] = useState(false);
  const [enableOcrImages, setEnableOcrImages] = useState(false);
  const [enableOcrPdfs, setEnableOcrPdfs] = useState(false);
  const [enableJavaScript, setEnableJavaScript] = useState(false); // ⚡ Attiva JS (separato da Playwright!)
  const [crawlerBatchSize, setCrawlerBatchSize] = useState(32); // ⚡ Batch Size crawler (1-100)
  const [useFastMode, setUseFastMode] = useState(true); // 🚀 default ON: Scrapy 10x più veloce per siti statici; disattiva per usare Playwright (SPA/JS)
  const [isDetectingSuggestion, setIsDetectingSuggestion] = useState(false);
  const [suggestionMessage, setSuggestionMessage] = useState<string | null>(null);
  
  // 🔄 Crawling Attivi (background tasks)
  const [activeCrawls, setActiveCrawls] = useState<any[]>([]);
  
  // 📦 Processi in background (PDF indicizzazione) – persistono anche chiudendo la pagina
  const [backgroundPdfJobs, setBackgroundPdfJobs] = useState<any[]>([]);
  const [runPdfInBackground, setRunPdfInBackground] = useState(false);
  const [showBackgroundPanel, setShowBackgroundPanel] = useState(true);
  
  // 🔍 Inspect Index Modal
  const [inspectData, setInspectData] = useState<any>(null);
  const [showInspectModal, setShowInspectModal] = useState(false);
  
  // 📋 SISTEMA A 2 FASI (come vanilla)
  type UploadedPdf = {
    filename: string;
    size_mb: number;
    modified: number;
  };
  
  const [uploadedPdfs, setUploadedPdfs] = useState<UploadedPdf[]>([]);
  const [selectedPdfFiles, setSelectedPdfFiles] = useState<Set<string>>(new Set());
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [isLoadingPdfs, setIsLoadingPdfs] = useState(false);
  const [indexName, setIndexName] = useState('');

  // 🔍 Stati per Preview e Analisi PDF
  const [selectedPdfForPreview, setSelectedPdfForPreview] = useState('');
  const [displayCountPreview, setDisplayCountPreview] = useState(3);
  const [previewResult, setPreviewResult] = useState<string>('');
  const [previewStats, setPreviewStats] = useState<any>(null);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [isGeneratingAdvanced, setIsGeneratingAdvanced] = useState(false);
  const [isExtractingOCR, setIsExtractingOCR] = useState(false);
  const [ocrMethod, setOcrMethod] = useState<'tesseract' | 'llava' | 'claude-vision'>('tesseract');
  const [_ocrResult, setOcrResult] = useState<any>(null);

  useEffect(() => {
    // 🔌 Verifica connessione backend all'avvio
    const checkBackendConnection = async () => {
      try {
        const response = await fetch(`${API_URL}/`, {
          method: 'GET',
          signal: AbortSignal.timeout(3000)
        });
        if (response.ok) {
          setBackendStatus('online');
          console.log('✅ Backend connesso');
        } else {
          setBackendStatus('offline');
          console.warn('⚠️ Backend risponde ma con errore');
        }
      } catch (error) {
        setBackendStatus('offline');
        console.error('❌ Backend non raggiungibile:', error);
      }
    };
    
    checkBackendConnection();
    loadIndices();
    loadUploadedPdfs(); // 🆕 Carica anche la lista PDF
    loadActiveCrawls(); // 🆕 Carica crawling attivi
    loadBackgroundJobs(); // 📦 Carica job PDF in background
    
    // Cleanup polling interval on unmount
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);
  
  // 🔄 Polling crawling attivi e processi PDF ogni 10 secondi
  useEffect(() => {
    const interval = setInterval(() => {
      loadActiveCrawls();
      loadBackgroundJobs();
    }, 10000); // 10 secondi
    
    return () => clearInterval(interval);
  }, []);

  // ⏱️ Aggiorna timer ogni secondo quando c'è un'indicizzazione in corso
  useEffect(() => {
    let timer: ReturnType<typeof setInterval>;
    if (processingIndex && indexingStartTime) {
      timer = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - indexingStartTime) / 1000));
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [processingIndex, indexingStartTime]);

  // 🔄 Applica configurazione esterna quando viene passata dalla cronologia
  useEffect(() => {
    if (externalConfig) {
      console.log('📋 Applicazione configurazione esterna:', externalConfig);
      
      // URL e nome indice
      if (externalConfig.seeds && externalConfig.seeds.length > 0) {
        setCrawlUrl(externalConfig.seeds[0]); // Usa il primo seed
      }
      if (externalConfig.index_suffix) {
        setCrawlIndexSuffix(externalConfig.index_suffix);
      }
      
      // Parametri chunking
      if (externalConfig.chunk_size !== undefined) setChunkSize(externalConfig.chunk_size);
      if (externalConfig.overlap !== undefined) setChunkOverlap(externalConfig.overlap);
      if (externalConfig.use_semantic_chunking !== undefined) setUseSemanticChunking(externalConfig.use_semantic_chunking);
      if (externalConfig.similarity_threshold !== undefined) setSimilarityThreshold(externalConfig.similarity_threshold);
      if (externalConfig.min_chunk_chars !== undefined) setMinChunkChars(externalConfig.min_chunk_chars);
      if (externalConfig.max_chunk_chars !== undefined) setMaxChunkChars(externalConfig.max_chunk_chars);
      
      // Parametri crawler
      if (externalConfig.depth !== undefined) setCrawlDepth(externalConfig.depth);
      if (externalConfig.domain_filter !== undefined) setDomainFilter(externalConfig.domain_filter);
      if (externalConfig.crawl_focus_keywords) {
        setFocusKeywords(Array.isArray(externalConfig.crawl_focus_keywords) 
          ? externalConfig.crawl_focus_keywords.join(', ') 
          : externalConfig.crawl_focus_keywords);
      }
      if (externalConfig.batch_size !== undefined) setCrawlerBatchSize(externalConfig.batch_size);
      if (externalConfig.embed_model) setEmbedModel(externalConfig.embed_model);
      
      // Checkbox
      if (externalConfig.download_pdfs !== undefined) setDownloadPdfs(externalConfig.download_pdfs);
      if (externalConfig.download_images !== undefined) setDownloadImages(externalConfig.download_images);
      if (externalConfig.enable_ocr !== undefined) setEnableOcrImages(externalConfig.enable_ocr);
      if (externalConfig.enable_pdf_ocr !== undefined) setEnableOcrPdfs(externalConfig.enable_pdf_ocr);
      if (externalConfig.enable_javascript !== undefined) setEnableJavaScript(externalConfig.enable_javascript);
      if (externalConfig.use_playwright !== undefined) setUsePlaywright(externalConfig.use_playwright);
      
      alert('✅ Configurazione caricata! Modifica i parametri se necessario e avvia il crawling.');
    }
  }, [externalConfig]);

  // 📋 Carica stato coda
  // 🗑️ RIMOSSO - Sistema coda non più attivo (processo diretto in background)
  // const loadQueueStatus = async () => { ... }
  // const cancelJob = async (jobId: string) => { ... }

  /**
   * Carica lista indici disponibili
   * Endpoint: GET /api/flow/kb/list-indices
   * Proxy: pigna-studio (8001) → pigna-2 staging (8002)
   * Servizio RAG: pigna-2 fornisce tutti gli indici FAISS/Qdrant
   */
  const loadIndices = async () => {
    try {
      const response = await fetch(`${API_URL}/api/flow/kb/list-indices`);
      const data = await response.json();
      setIndices(data.indices || []);
      
      // Se stiamo aspettando un indice, controlla se è pronto
      if (processingIndex) {
        const foundIndex = data.indices.find((idx: KnowledgeIndex) => idx.id === processingIndex);
        if (foundIndex && foundIndex.status === 'ready') {
          // Indice completato!
          const indexName = processingIndex;
          const chunksCount = foundIndex.chunks_count;
          
          setProcessingIndex(null);
          
          if (pollingInterval) {
            clearInterval(pollingInterval);
            setPollingInterval(null);
          }
          
          alert(`✅ Indicizzazione completata!\n\nIndice: "${indexName}"\nChunks: ${chunksCount}\n\nL'indice è ora pronto per essere usato nei tuoi flussi.`);
        }
      }
    } catch (error) {
      console.error('Errore caricamento indici:', error);
    }
  };
  
  /**
   * Carica lista PDF caricati (non ancora indicizzati)
   * Endpoint: GET /api/flow/kb/list-uploaded-pdfs
   * Proxy: pigna-studio (8001) → pigna-2 staging (8002)
   * Servizio RAG: pigna-2 gestisce lo storage PDF in UPLOAD_DIR
   */
  const loadUploadedPdfs = async () => {
    setIsLoadingPdfs(true);
    try {
      const response = await fetch(`${API_URL}/api/flow/kb/list-uploaded-pdfs`);
      const data = await response.json();
      setUploadedPdfs(data.files || []);
      console.log(`📋 Caricati ${data.count} PDF dal server`);
    } catch (error) {
      console.error('Errore caricamento PDF:', error);
    } finally {
      setIsLoadingPdfs(false);
    }
  };
  
  // 🔄 Carica crawling attivi
  const loadActiveCrawls = async () => {
    try {
      const response = await fetch(`${API_URL}/api/crawl/status`);
      if (!response.ok) {
        console.warn('Endpoint /api/crawl/status non disponibile');
        setActiveCrawls([]);
        return;
      }
      const data = await response.json();
      setActiveCrawls(data.active_crawls || []);
      console.log(`🔄 Crawling attivi: ${data.active_crawls?.length || 0}`);
    } catch (error) {
      console.error('Errore caricamento crawling attivi:', error);
      setActiveCrawls([]);
    }
  };

  // 📦 Carica job PDF in background (persistono anche chiudendo la pagina)
  // Usa /api/studio/* per evitare proxy verso 8002 che restituirebbe 405
  const loadBackgroundJobs = async () => {
    if (backgroundJobsEndpointAvailable.current === false) return; // Endpoint non disponibile (404), evita richieste ripetute
    try {
      const response = await fetch(`${API_URL}/api/studio/background-jobs`);
      if (response.status === 404) {
        backgroundJobsEndpointAvailable.current = false;
        setBackgroundPdfJobs([]);
        return;
      }
      if (!response.ok) return;
      backgroundJobsEndpointAvailable.current = true;
      const data = await response.json();
      setBackgroundPdfJobs(data.jobs || []);
    } catch (error) {
      console.warn('Errore caricamento processi background:', error);
      setBackgroundPdfJobs([]);
    }
  };

  // 🧹 Pulisci processi in background (solo completati/falliti o tutti)
  // Usa /api/studio/* per evitare proxy verso 8002 e 405
  const purgeBackgroundJobs = async (completedOnly: boolean) => {
    try {
      const url = `${API_URL}/api/studio/background-jobs/purge`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ completed_only: completedOnly }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      alert(data.message || (completedOnly ? 'Rimossi i job completati/falliti.' : 'Lista processi svuotata.'));
      loadBackgroundJobs();
    } catch (error: any) {
      console.error('Errore purge processi background:', error);
      alert(`❌ Errore: ${error?.message || error}`);
    }
  };

  // 🛑 Stoppa un crawling
  const stopCrawl = async (crawlId: string) => {
    try {
      const response = await fetch(`${API_URL}/api/crawl/stop/${crawlId}`, {
        method: 'POST'
      });
      if (response.ok) {
        alert(`✅ Crawling ${crawlId} fermato!`);
        loadActiveCrawls(); // Ricarica lista
      } else {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (error: any) {
      console.error('Errore stop crawling:', error);
      alert(`❌ Errore: ${error.message}`);
    }
  };
  
  // 🛑 Stoppa TUTTI i crawling
  const stopAllCrawls = async () => {
    if (!confirm('⚠️ Vuoi fermare TUTTI i crawling attivi?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/crawl/stop-all`, {
        method: 'POST'
      });
      if (response.ok) {
        alert('✅ Tutti i crawling fermati!');
        loadActiveCrawls();
      } else {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (error: any) {
      console.error('Errore stop all:', error);
      alert(`❌ Errore: ${error.message}`);
    }
  };
  
  const handleUploadOnly = async () => {
    if (pendingFiles.length === 0) {
      alert('❌ Nessun file selezionato!');
      return;
    }
    
    setIsUploadingPDF(true);
    try {
      const formData = new FormData();
      pendingFiles.forEach(file => {
        formData.append('files', file);
      });
      
      const response = await fetch(`${API_URL}/api/flow/kb/upload-pdf-only`, {
        method: 'POST',
        body: formData,
      });
      
      if (response.ok) {
        const result = await response.json();
        alert(`✅ Upload completato!\n\n${result.saved_count} PDF caricati sul server\n${result.failed_count} errori`);
        
        // Reset pending files
        setPendingFiles([]);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        
        // Ricarica lista
        loadUploadedPdfs();
      } else {
        throw new Error('Upload fallito');
      }
    } catch (error: any) {
      alert(`❌ Errore upload: ${error.message}`);
      console.error('Upload error:', error);
    } finally {
      setIsUploadingPDF(false);
    }
  };
  
  const handleDeleteSelected = async () => {
    if (selectedPdfFiles.size === 0) {
      alert('❌ Nessun file selezionato!');
      return;
    }
    
    if (!confirm(`🗑️ Vuoi eliminare ${selectedPdfFiles.size} file?`)) {
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/flow/kb/delete-pdfs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filenames: Array.from(selectedPdfFiles)
        }),
      });
      
      if (response.ok) {
        const result = await response.json();
        alert(`✅ ${result.deleted_count} file eliminati`);
        
        // Reset selezione
        setSelectedPdfFiles(new Set());
        
        // Ricarica lista
        loadUploadedPdfs();
      } else {
        throw new Error('Eliminazione fallita');
      }
    } catch (error: any) {
      alert(`❌ Errore eliminazione: ${error.message}`);
      console.error('Delete error:', error);
    }
  };
  
  const handleGenerateIndex = async () => {
    console.log('🟢 [FRONTEND] handleGenerateIndex START');
    console.log('📊 [FRONTEND] selectedPdfFiles:', Array.from(selectedPdfFiles));
    console.log('📊 [FRONTEND] indexName:', indexName);
    
    if (selectedPdfFiles.size === 0) {
      alert('❌ Nessun file selezionato!');
      return;
    }
    
    if (!indexName.trim()) {
      alert('❌ Inserisci un nome per l\'indice!');
      return;
    }

    // ⚠️ CONFERMA ESPLICITA: evita avvio indicizzazione per errore (es. volevi solo fare una query)
    const fileCount = selectedPdfFiles.size;
    const confirmed = window.confirm(
      `⚠️ Indicizzazione documenti\n\nStai per avviare l'indicizzazione di ${fileCount} file nell'indice "${indexName.trim()}".\n\nQuesto creerà un processo in background (non è una semplice query all'indice).\n\nContinuare?`
    );
    if (!confirmed) return;

    // 🔍 VERIFICA CONNETTIVITÀ BACKEND PRIMA DI INIZIARE
    console.log('🔍 [FRONTEND] Verifica connettività backend...');
    try {
      const healthCheck = await fetch(`${API_URL}/`, { 
        method: 'GET',
        signal: AbortSignal.timeout(3000) // Timeout 3 secondi
      });
      
      if (!healthCheck.ok) {
        throw new Error('Backend non risponde correttamente');
      }
      
      console.log('✅ [FRONTEND] Backend connesso');
    } catch (healthError: any) {
      console.error('❌ [FRONTEND] Backend non raggiungibile:', healthError);
      const isLocalhost = typeof window !== 'undefined' && window.location.hostname === 'localhost';
      if (isLocalhost) {
        alert(`❌ ERRORE DI CONNESSIONE\n\nIl server backend (porta 8001) non è raggiungibile!\n\nDettagli: ${healthError.message}\n\nVerifica che il backend sia avviato con:\ncd /Users/fm/rag-simple/pigna-studio && ./start-backend-dev.sh`);
      } else {
        alert(`❌ ERRORE DI CONNESSIONE\n\nIl server backend non è raggiungibile!\n\nDettagli: ${healthError.message}\n\nContatta l'amministratore per verificare lo stato del servizio.`);
      }
      return;
    }
    
    console.log('🚀 [FRONTEND] Chiamata API generate-index...');
    
    try {
      const response = await fetch(`${API_URL}/api/flow/kb/generate-index`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filenames: Array.from(selectedPdfFiles),
          index_name: indexName.trim(),
          embed_model: embedModel,
          extractor: extractorType,
          chunk_size: chunkSize,
          overlap: chunkOverlap,
          use_semantic_chunking: useSemanticChunking,
          similarity_threshold: similarityThreshold,
          min_chunk_chars: minChunkChars,
          max_chunk_chars: maxChunkChars,
          use_ocr_fallback: enableOCR,
          use_presidio: usePresidio,
          enable_multimodal: enableMultiModal,
          background: runPdfInBackground,
        }),
      });
      
      console.log('✅ [FRONTEND] Response ricevuta:', response.status);
      
      if (response.ok) {
        const result = await response.json();
        console.log('📦 [FRONTEND] Result:', result);
        
        if (result.background) {
          alert(`✅ Indicizzazione avviata in background!\n\n🆔 Job: ${result.job_id}\n🗂️ Indice: ${result.index_name}\n📄 ${result.files_count} PDF\n\n💡 Puoi chiudere la pagina; vedi "Processi in background" per lo stato.`);
          setIndexName('');
          setSelectedPdfFiles(new Set());
          loadBackgroundJobs();
          loadIndices();
          setShowBackgroundPanel(true);
        } else {
          alert(`✅ Indicizzazione avviata!\n\n${result.files_count} PDF in elaborazione per indice "${result.index_name}"`);
          setIndexName('');
          setSelectedPdfFiles(new Set());
          startPollingForIndex(result.index_name, result.files_count);
          loadIndices();
        }
      } else {
        const error = await response.json();
        console.error('❌ [FRONTEND] Errore backend:', error);
        throw new Error(error.detail || 'Generazione indice fallita');
      }
    } catch (error: any) {
      console.error('❌ [FRONTEND] Exception:', error);
      alert(`❌ Errore: ${error.message}`);
      console.error('Generate index error:', error);
    }
  };
  
  // Helper functions per selezione
  const handleSelectAll = () => {
    setSelectedPdfFiles(new Set(uploadedPdfs.map(pdf => pdf.filename)));
  };
  
  const handleDeselectAll = () => {
    setSelectedPdfFiles(new Set());
  };
  
  const togglePdfSelection = (filename: string) => {
    const newSelection = new Set(selectedPdfFiles);
    if (newSelection.has(filename)) {
      newSelection.delete(filename);
    } else {
      newSelection.add(filename);
    }
    setSelectedPdfFiles(newSelection);
  };

  // 🔍 GENERA ANTEPRIMA CHUNKS
  const handleGeneratePreview = async () => {
    console.log('🔍 Avvio generazione anteprima chunks...');
    
    if (!selectedPdfForPreview) {
      alert('❌ Seleziona un PDF per generare l\'anteprima!');
      return;
    }

    // Verifica che il file esista nella lista aggiornata
    const fileExists = uploadedPdfs.some(pdf => pdf.filename === selectedPdfForPreview);
    if (!fileExists) {
      console.warn('⚠️ File selezionato non trovato nella lista, ricarico...');
      await loadUploadedPdfs();
      const stillMissing = !uploadedPdfs.some(pdf => pdf.filename === selectedPdfForPreview);
      if (stillMissing) {
        alert(`❌ Il file "${selectedPdfForPreview}" non è più disponibile.\n\nPer favore seleziona un altro file dalla lista.`);
        setSelectedPdfForPreview('');
        return;
      }
    }

    setIsGeneratingPreview(true);
    setPreviewResult('⏳ Generazione anteprima in corso...');
    setPreviewStats(null);

    try {
      const requestData = {
        filename: selectedPdfForPreview,
        display_count: displayCountPreview,
        embed_model: embedModel,
        use_semantic_chunking: useSemanticChunking,
        chunk_size: chunkSize,
        chunk_overlap: chunkOverlap,
        min_chunk_chars: minChunkChars,
        max_chunk_chars: maxChunkChars,
        similarity_threshold: similarityThreshold,
        extractor: extractorType
      };

      console.log('🔍 Invio richiesta anteprima:', requestData);

      const response = await fetch(`${API_URL}/api/flow/kb/preview-chunks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        // Estrai messaggio di errore dal body se disponibile
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = await response.json();
          if (errorData.detail) {
            errorMessage = errorData.detail;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch {
          // Ignora se non è JSON
        }
        
        // Se file non trovato, ricarica lista PDF
        if (response.status === 404) {
          console.warn('⚠️ File non trovato, ricarico lista PDF...');
          await loadUploadedPdfs();
        }
        
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('✅ Risultato anteprima ricevuto:', result);

      if (result.error) {
        throw new Error(result.error);
      }

      // Mostra statistiche
      setPreviewStats(result);

      // Mostra chunks
      let previewText = `📄 ANTEPRIMA CHUNKS - ${result.filename}\n`;
      previewText += `${'='.repeat(80)}\n\n`;

      if (result.chunks) {
        result.chunks.forEach((chunk: string, index: number) => {
          previewText += `🔹 CHUNK ${index + 1}/${result.displayed_chunks} (${chunk.length} caratteri)\n`;
          previewText += `${'-'.repeat(60)}\n`;
          previewText += `${chunk}\n\n`;
        });

        if (result.total_chunks > result.displayed_chunks) {
          previewText += `\n📝 NOTA: Mostrati ${result.displayed_chunks} di ${result.total_chunks} chunks totali.\n`;
          previewText += `Aumenta il numero di "Chunks da visualizzare" per vedere di più.`;
        }
      }

      setPreviewResult(previewText);
      alert('✅ Anteprima generata con successo!');

    } catch (error: any) {
      console.error('❌ Errore generazione anteprima:', error);
      setPreviewResult(`❌ ERRORE:\n${error.message}`);
      alert(`❌ Errore durante la generazione dell'anteprima:\n${error.message}`);
    } finally {
      setIsGeneratingPreview(false);
    }
  };

  // 🧠 GENERA ANALISI AVANZATA
  const handleGenerateAdvancedPreview = async () => {
    console.log('🧠 Avvio anteprima preprocessing avanzato...');

    if (!selectedPdfForPreview) {
      alert('❌ Seleziona un PDF per l\'analisi avanzata!');
      return;
    }

    // Verifica che il file esista nella lista aggiornata
    const fileExists = uploadedPdfs.some(pdf => pdf.filename === selectedPdfForPreview);
    if (!fileExists) {
      console.warn('⚠️ File selezionato non trovato nella lista, ricarico...');
      await loadUploadedPdfs();
      const stillMissing = !uploadedPdfs.some(pdf => pdf.filename === selectedPdfForPreview);
      if (stillMissing) {
        alert(`❌ Il file "${selectedPdfForPreview}" non è più disponibile.\n\nPer favore seleziona un altro file dalla lista.`);
        setSelectedPdfForPreview('');
        return;
      }
    }

    setIsGeneratingAdvanced(true);
    setPreviewResult('⏳ Analisi preprocessing avanzato in corso...');
    setPreviewStats(null);

    try {
      const requestData = {
        filename: selectedPdfForPreview,
        display_count: displayCountPreview,
        extractor: extractorType,
        embed_model: embedModel,
        enable_ocr: enableOCR
      };

      console.log('🧠 Invio richiesta preview avanzato:', requestData);

      const response = await fetch(`${API_URL}/api/flow/kb/preview-advanced`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        // Estrai messaggio di errore dal body se disponibile
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = await response.json();
          if (errorData.detail) {
            errorMessage = errorData.detail;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch {
          // Ignora se non è JSON
        }
        
        // Se file non trovato, ricarica lista PDF
        if (response.status === 404) {
          console.warn('⚠️ File non trovato, ricarico lista PDF...');
          await loadUploadedPdfs();
        }
        
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('🧠 Risposta preview avanzato:', data);

      // Mostra statistiche preprocessing
      setPreviewStats(data);

      // Mostra preview chunks con entità
      let previewContent = `🧠 ANTEPRIMA PREPROCESSING AVANZATO\n`;
      previewContent += `📄 File: ${data.filename}\n`;
      previewContent += `📊 Totale chunks: ${data.total_chunks}\n\n`;

      // Top entità trovate
      if (data.top_entities?.persons?.length > 0) {
        previewContent += `👥 PERSONE TROVATE:\n${data.top_entities.persons.join(', ')}\n\n`;
      }

      if (data.top_entities?.organizations?.length > 0) {
        previewContent += `🏢 ORGANIZZAZIONI TROVATE:\n${data.top_entities.organizations.join(', ')}\n\n`;
      }

      if (data.top_entities?.laws?.length > 0) {
        previewContent += `📜 LEGGI/ARTICOLI TROVATI:\n${data.top_entities.laws.join(', ')}\n\n`;
      }

      previewContent += `📝 CHUNKS CON ENTITÀ:\n`;
      previewContent += `${'='.repeat(80)}\n\n`;

      if (data.chunks_preview) {
        data.chunks_preview.forEach((chunk: any) => {
          previewContent += `CHUNK ${chunk.index + 1} (pos: ${chunk.start_pos}, frasi: ${chunk.sentence_count})\n`;
          previewContent += `${'─'.repeat(40)}\n`;
          previewContent += `${chunk.text}\n\n`;

          if (chunk.entities?.persons?.length > 0) {
            previewContent += `👥 Persone: ${chunk.entities.persons.join(', ')}\n`;
          }
          if (chunk.entities?.organizations?.length > 0) {
            previewContent += `🏢 Organizzazioni: ${chunk.entities.organizations.join(', ')}\n`;
          }
          if (chunk.entities?.laws?.length > 0) {
            previewContent += `📜 Leggi: ${chunk.entities.laws.join(', ')}\n`;
          }
          if (chunk.entities?.dates?.length > 0) {
            previewContent += `📅 Date: ${chunk.entities.dates.join(', ')}\n`;
          }

          previewContent += `\n`;
        });
      }

      setPreviewResult(previewContent);
      alert('✅ Analisi avanzata completata!');

    } catch (error: any) {
      console.error('❌ Errore analisi avanzata:', error);
      setPreviewResult(`❌ ERRORE:\n${error.message}`);
      alert(`❌ Errore durante l'analisi avanzata:\n${error.message}`);
    } finally {
      setIsGeneratingAdvanced(false);
    }
  };

  // 🔍 OCR Extraction con metodo selezionabile
  const handleOCRExtract = async () => {
    console.log(`🔍 Avvio estrazione OCR con metodo: ${ocrMethod}...`);

    if (!selectedPdfForPreview) {
      alert('❌ Seleziona un PDF per l\'estrazione OCR!');
      return;
    }

    setIsExtractingOCR(true);
    setPreviewResult('⏳ Estrazione testo con OCR in corso...');
    setOcrResult(null);

    try {
      const requestData = {
        filename: selectedPdfForPreview,
        method: ocrMethod,
        language: 'ita+eng',
        max_pages: ocrMethod === 'tesseract' ? 50 : 10  // Limita pagine per Visual LLM (lenti)
      };

      console.log('🔍 Invio richiesta OCR:', requestData);

      const response = await fetch(`${API_URL}/api/ocr/extract`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('✅ Risultato OCR ricevuto:', data);

      setOcrResult(data);

      // Mostra risultati
      let resultContent = `🔍 ESTRAZIONE OCR COMPLETATA\n\n`;
      resultContent += `📄 File: ${data.filename}\n`;
      resultContent += `⚙️ Metodo: ${data.method === 'tesseract' ? '📝 Tesseract OCR' : data.method === 'llava' ? '🏠 Visual LLM Locale (LLaVA)' : '🤖 Visual LLM Cloud'}\n`;
      resultContent += `📊 Pagine processate: ${data.pages_processed}${data.total_pages ? `/${data.total_pages}` : ''}\n`;
      resultContent += `📏 Testo estratto: ${data.text_length.toLocaleString()} caratteri\n\n`;

      if (data.method === 'llava' || data.method === 'claude-vision') {
        resultContent += `🤖 AI Model: ${data.model || 'Advanced Vision Model'}\n`;
        if (data.note) {
          resultContent += `💡 ${data.note}\n`;
        }
        resultContent += `\n`;
      }

      resultContent += `📝 TESTO ESTRATTO (preview):\n`;
      resultContent += `${'='.repeat(50)}\n`;
      resultContent += data.text_preview || data.full_text?.substring(0, 3000) || 'Nessun testo estratto';

      setPreviewResult(resultContent);
      alert(`✅ Estrazione OCR completata!\n\n${data.pages_processed} pagine processate\n${data.text_length.toLocaleString()} caratteri estratti`);

    } catch (error: any) {
      console.error('❌ Errore estrazione OCR:', error);
      setPreviewResult(`❌ ERRORE OCR:\n${error.message}`);
      alert(`❌ Errore durante estrazione OCR:\n${error.message}`);
    } finally {
      setIsExtractingOCR(false);
    }
  };
  
  const startPollingForIndex = (indexName: string, filesCount: number = 1) => {
    setProcessingIndex(indexName);
    setDetailedStatus(null); // Reset
    setIndexingStartTime(Date.now()); // Avvia timer
    setElapsedTime(0); // Reset timer
    
    // 🔄 Polling dettagliato ogni 800ms per aggiornamenti in tempo reale
    let consecutiveErrors = 0;
    let consecutiveNotFound = 0; // Contatore per "not_found" consecutivi
    const maxConsecutiveErrors = 5; // Ferma dopo 5 errori consecutivi
    const maxConsecutiveNotFound = 10; // Aspetta fino a 10 "not_found" prima di fermarsi (8 secondi)
    const startTime = Date.now();
    const gracePeriod = 5000; // Periodo di grazia: 5 secondi prima di considerare "not_found" come errore
    
    const interval = setInterval(async () => {
      try {
        // 📊 Chiama endpoint di status dettagliato
        const statusResponse = await fetch(`${API_URL}/api/flow/kb/indexing-status/${indexName}`);
        
        // 🐛 Se 404, verifica se siamo nel periodo di grazia
        if (statusResponse.status === 404) {
          const elapsed = Date.now() - startTime;
          if (elapsed < gracePeriod) {
            // Siamo ancora nel periodo di grazia, aspetta
            console.log(`⏳ Status non ancora disponibile (${Math.floor(elapsed/1000)}s), aspetto...`);
            consecutiveNotFound++;
            if (consecutiveNotFound >= maxConsecutiveNotFound) {
              console.warn(`⚠️ Indice "${indexName}" non trovato dopo ${maxConsecutiveNotFound} tentativi. Verifico se esiste nella lista...`);
              // Verifica se l'indice esiste nella lista (potrebbe essere completato)
              try {
                const indicesResponse = await fetch(`${API_URL}/api/flow/kb/list-indices`);
                if (indicesResponse.ok) {
                  const indicesData = await indicesResponse.json();
                  const foundIndex = indicesData.indices?.find((idx: any) => idx.name === indexName);
                  if (foundIndex) {
                    console.log('✅ Indice trovato nella lista! Indicizzazione completata.');
                    clearInterval(interval);
                    setPollingInterval(null);
                    setProcessingIndex(null);
                    setDetailedStatus(null);
                    setIndexingStartTime(null);
                    await loadIndices();
                    alert(`✅ Indicizzazione completata!\n\nIndice "${indexName}" creato con ${foundIndex.chunks_count} chunks.`);
                    return;
                  }
                }
              } catch (e) {
                console.error('Errore verifica indice:', e);
              }
              // Se non trovato, ferma polling
              clearInterval(interval);
              setPollingInterval(null);
              setProcessingIndex(null);
              setDetailedStatus(null);
              setIndexingStartTime(null);
              await loadIndices();
              return;
            }
            return; // Continua a pollare
          } else {
            // Periodo di grazia scaduto, verifica se l'indice esiste
            console.warn(`⚠️ Indice "${indexName}" non trovato (404) dopo periodo di grazia. Verifico se esiste nella lista...`);
            try {
              const indicesResponse = await fetch(`${API_URL}/api/flow/kb/list-indices`);
              if (indicesResponse.ok) {
                const indicesData = await indicesResponse.json();
                const foundIndex = indicesData.indices?.find((idx: any) => idx.name === indexName);
                if (foundIndex) {
                  console.log('✅ Indice trovato nella lista! Indicizzazione completata.');
                  clearInterval(interval);
                  setPollingInterval(null);
                  setProcessingIndex(null);
                  setDetailedStatus(null);
                  setIndexingStartTime(null);
                  await loadIndices();
                  alert(`✅ Indicizzazione completata!\n\nIndice "${indexName}" creato con ${foundIndex.chunks_count} chunks.`);
                  return;
                }
              }
            } catch (e) {
              console.error('Errore verifica indice:', e);
            }
            // Se non trovato, ferma polling
            clearInterval(interval);
            setPollingInterval(null);
            setProcessingIndex(null);
            setDetailedStatus(null);
            setIndexingStartTime(null);
            await loadIndices();
            return;
          }
        }
        
        if (statusResponse.ok) {
          consecutiveErrors = 0; // Reset contatore errori
          consecutiveNotFound = 0; // Reset contatore not_found
          const statusData = await statusResponse.json();
          
          if (statusData.status === 'processing' && statusData.details) {
            // 🎉 Controlla se è completato prima di aggiornare
            if (statusData.details.phase === 'completed') {
              console.log('✅ Indicizzazione completata! (phase=completed)');
              clearInterval(interval);
              setPollingInterval(null);
              setProcessingIndex(null);
              setDetailedStatus(null);
              setIndexingStartTime(null);
              await loadIndices(); // Aggiorna la lista
              alert(`✅ Indicizzazione completata!\n\nIndice "${indexName}" creato con ${statusData.details.total_chunks} chunks.`);
              return; // Esci subito
            }
            
            // Aggiorna stato dettagliato
            setDetailedStatus(statusData.details);
            console.log('📊 Status update:', statusData.details);
          } else if (statusData.status === 'not_found') {
            // Status non trovato = indicizzazione completata o non ancora iniziata
            // Verifica se l'indice è nella lista (= completato)
            console.log('🔍 Status non trovato, verifico se indice è stato creato...');
            
            const indicesResponse = await fetch(`${API_URL}/api/flow/kb/list-indices`);
            if (indicesResponse.ok) {
              const indicesData = await indicesResponse.json();
              const foundIndex = indicesData.indices?.find((idx: any) => idx.name === indexName);
              
              if (foundIndex && foundIndex.status === 'ready') {
                // 🎉 L'indice è stato creato con successo!
                console.log('✅ Indice trovato nella lista! Indicizzazione completata.');
                clearInterval(interval);
                setPollingInterval(null);
                setProcessingIndex(null);
                setDetailedStatus(null);
                setIndexingStartTime(null);
                await loadIndices(); // Aggiorna la lista
                alert(`✅ Indicizzazione completata!\n\nIndice "${indexName}" creato con ${foundIndex.chunks_count} chunks.`);
                return;
              } else {
                // Indice non trovato - potrebbe essere ancora in elaborazione
                const elapsed = Date.now() - startTime;
                if (elapsed < gracePeriod) {
                  // Siamo ancora nel periodo di grazia, continua a pollare
                  console.log(`⏳ Indice non ancora nella lista (${Math.floor(elapsed/1000)}s), continuo polling...`);
                  return;
                }
                consecutiveNotFound++;
                if (consecutiveNotFound >= maxConsecutiveNotFound) {
                  console.warn(`⚠️ Indice "${indexName}" non trovato dopo ${maxConsecutiveNotFound} tentativi. Fermo polling.`);
                  clearInterval(interval);
                  setPollingInterval(null);
                  setProcessingIndex(null);
                  setDetailedStatus(null);
                  setIndexingStartTime(null);
                }
              }
            }
          }
        } else if (statusResponse.status === 404) {
          // 🔒 GESTIONE 404: Indice non trovato - verifica se esiste nella lista
          consecutiveErrors++;
          console.warn(`⚠️ 404 per indice "${indexName}" (tentativo ${consecutiveErrors}/${maxConsecutiveErrors})`);
          
          // Verifica se l'indice esiste nella lista (potrebbe essere completato)
          try {
            const indicesResponse = await fetch(`${API_URL}/api/flow/kb/list-indices`);
            if (indicesResponse.ok) {
              const indicesData = await indicesResponse.json();
              const foundIndex = indicesData.indices?.find((idx: any) => idx.name === indexName);
              
              if (foundIndex) {
                // Indice esiste ma status endpoint non disponibile - probabilmente completato
                console.log('✅ Indice trovato nella lista! Status endpoint non disponibile, ma indice esiste.');
                clearInterval(interval);
                setPollingInterval(null);
                setProcessingIndex(null);
                setDetailedStatus(null);
                setIndexingStartTime(null);
                await loadIndices();
                return;
              }
            }
          } catch (e) {
            console.error('Errore verifica indice:', e);
          }
          
          // Se dopo maxConsecutiveErrors tentativi non troviamo l'indice, fermiamo
          if (consecutiveErrors >= maxConsecutiveErrors) {
            console.warn(`⚠️ Indice "${indexName}" non trovato dopo ${maxConsecutiveErrors} tentativi 404. Fermo polling.`);
            clearInterval(interval);
            setPollingInterval(null);
            setProcessingIndex(null);
            setDetailedStatus(null);
            setIndexingStartTime(null);
          }
        } else {
          // Altri errori HTTP
          consecutiveErrors++;
          console.error(`❌ Errore HTTP ${statusResponse.status} per indice "${indexName}"`);
          if (consecutiveErrors >= maxConsecutiveErrors) {
            console.warn(`⚠️ Troppi errori per indice "${indexName}". Fermo polling.`);
            clearInterval(interval);
            setPollingInterval(null);
            setProcessingIndex(null);
            setDetailedStatus(null);
            setIndexingStartTime(null);
          }
        }
      } catch (error) {
        consecutiveErrors++;
        console.error('❌ Errore polling status:', error);
        if (consecutiveErrors >= maxConsecutiveErrors) {
          console.warn(`⚠️ Troppi errori di rete per indice "${indexName}". Fermo polling.`);
          clearInterval(interval);
          setPollingInterval(null);
          setProcessingIndex(null);
          setDetailedStatus(null);
          setIndexingStartTime(null);
        }
      }
    }, 800); // Polling veloce per aggiornamenti fluidi
    
    setPollingInterval(interval);
    
    // Timeout dinamico basato su numero file (min 5 min, max 24 ore)
    const timeoutMs = Math.min(86400000, Math.max(300000, filesCount * 3500 * 2)); // 2x stima
    setTimeout(() => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
        setProcessingIndex(null);
        setDetailedStatus(null);
        setIndexingStartTime(null);
        alert('⏱️ Timeout: L\'indicizzazione sta impiegando più del previsto. L\'indice potrebbe essere ancora in elaborazione in background. Ricarica la pagina per verificare.');
      }
    }, timeoutMs);
  };

  // 🗂️ Funzione ricorsiva per leggere file da cartelle trascinate
  const readEntriesRecursively = async (entry: any): Promise<File[]> => {
    const files: File[] = [];
    
    if (entry.isFile) {
      // È un file, lo aggiungo
      const file: File = await new Promise((resolve, reject) => {
        entry.file(resolve, reject);
      });
      
      const ext = file.name.toLowerCase();
      if (file.type === 'application/pdf' || ext.endsWith('.pdf') || ext.endsWith('.txt') || ext.endsWith('.md')) {
        files.push(file);
      }
    } else if (entry.isDirectory) {
      // È una cartella, leggo ricorsivamente
      const dirReader = entry.createReader();
      const allEntries: any[] = [];
      
      // 🔄 LOOP per superare il limite di 100 entry per chiamata
      // Chrome limita readEntries() a 100 entry alla volta!
      let entries: any[] = [];
      do {
        entries = await new Promise((resolve, reject) => {
          dirReader.readEntries(resolve, reject);
        });
        allEntries.push(...entries);
        console.log(`📂 Letti ${entries.length} entry da ${entry.name} (totale: ${allEntries.length})`);
      } while (entries.length > 0); // Continua finché ci sono entry
      
      console.log(`✅ Totale ${allEntries.length} entry in ${entry.name}`);
      
      // Processa ricorsivamente ogni entry
      for (const subEntry of allEntries) {
        const subFiles = await readEntriesRecursively(subEntry);
        files.push(...subFiles);
      }
    }
    
    return files;
  };


  // 🌐 Funzione per avviare crawling con STREAMING (real-time updates)
  const handleCrawlWithStreaming = async () => {
    // ✅ VALIDAZIONE PARAMETRI
    if (!crawlUrl.trim() || !webIndexName.trim()) {
      alert('❌ Inserisci URL e nome indice!');
      return;
    }

    // Valida URL
    const urls = crawlUrl.split('\n').map(url => url.trim()).filter(url => url);
    const invalidUrls = urls.filter(url => {
      try {
        new URL(url);
        return false;
      } catch {
        return true;
      }
    });

    if (invalidUrls.length > 0) {
      alert(`❌ URL non validi:\n${invalidUrls.join('\n')}\n\nInserisci URL completi (es. https://example.com)`);
      return;
    }

    // Valida nome indice (solo alfanumerici, underscore, trattini)
    if (!/^[a-zA-Z0-9_-]+$/.test(webIndexName.trim())) {
      alert('❌ Nome indice non valido!\n\nUsa solo lettere, numeri, underscore (_) e trattini (-)\nEs: "mio-indice-2024"');
      return;
    }

    setIsCrawling(true);
    setShowLogPanel(true); // Trasparenza: apri il Log quando si avvia un crawl
    setCrawlLog([useFastMode ? '🚀 Avvio Fast Crawling con Scrapy...' : '🚀 Avvio crawling con streaming...']);
    setCrawlProgress({ pages_processed: 0, current_url: null, chunks_created: 0, phase: 'crawling' });
    
    // 🐛 Reset debug panel
    setCrawlDebug({
      urls_in_queue: urls,
      urls_visited: [],
      urls_processed: [],
      urls_failed: [],
      chunks_by_url: {},
      total_chunks: 0,
      errors: [],
      start_time: Date.now(),
      last_update: Date.now()
    });
    setShowDebugPanel(true); // Mostra automaticamente il pannello debug

    try {
      // 🚀 Fast Mode: usa Scrapy (10x più veloce per siti statici)
      if (useFastMode) {
        // 🔍 Verifica che il nome indice sia presente
        const finalIndexName = crawlIndexSuffix || webIndexName;
        if (!finalIndexName || !finalIndexName.trim()) {
          alert('❌ Errore: Nome indice mancante!\n\nInserisci un nome indice valido nel campo "Nome Indice" o "Suffisso Indice".');
          setIsCrawling(false);
          return;
        }
        
        const fastParams = {
          seeds: crawlUrl.split('\n').map(url => url.trim()).filter(url => url),
          max_depth: crawlDepth,
          mode: "auto", // auto-detect: scrapy o playwright
          embed_model: embedModel,
          index_suffix: finalIndexName.trim(), // ✅ Usa sempre un nome valido
          fast_mode: true, // 🚀 Attiva modalità veloce (32 concorrenti, 0.1s delay)
          domain_filter: domainFilter?.trim() || undefined, // 🔒 Solo link che iniziano con https?:// + questo dominio
          // 📦 Parametri chunking (dalle Impostazioni Avanzate)
          chunk_size: chunkSize,
          overlap: chunkOverlap,
          use_semantic_chunking: useSemanticChunking,
          similarity_threshold: similarityThreshold,
          min_chunk_chars: minChunkChars,
          max_chunk_chars: maxChunkChars
        };

        // 📊 Feedback visivo durante crawling
        setCrawlLog(prev => [
          ...prev,
          `🌐 Connessione a: ${fastParams.seeds[0]}`,
          `🔍 Modalità: Auto-detect (Scrapy/Playwright)`,
          `📏 Profondità massima: ${crawlDepth}`,
          `⏳ Crawling in corso... (può richiedere 20-30 secondi)`,
          `🕷️ Scrapy sta scaricando le pagine web...`,
          `💡 Nessun JavaScript - Solo HTML/CSS statico`
        ]);

        // 🔍 Verifica backend prima di fast crawl
        setCrawlLog(prev => [...prev, '🔍 Verifica connessione backend...']);
        try {
          const healthCheck = await fetch(`${API_URL}/health`, { method: 'GET' });
          if (!healthCheck.ok) {
            throw new Error('Backend non raggiungibile');
          }
        } catch (e) {
          setCrawlLog(prev => [...prev, '⚠️ Backend non raggiungibile, provo comunque...']);
        }

        setCrawlLog(prev => [...prev, '📡 Invio richiesta Fast Crawl...']);
        // Heartbeat nel pannello Log mentre si attende la risposta
        if (crawlHeartbeatRef.current) clearInterval(crawlHeartbeatRef.current);
        crawlHeartbeatRef.current = setInterval(() => {
          setCrawlLog(prev => [...prev, `⏳ [${new Date().toLocaleTimeString()}] Crawling in corso... (attesa backend)`]);
        }, 2500);

        const response = await fetch(`${API_URL}/api/crawl/fast`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(fastParams),
        });

        if (crawlHeartbeatRef.current) {
          clearInterval(crawlHeartbeatRef.current);
          crawlHeartbeatRef.current = null;
        }
        if (!response.ok) {
          const errorText = await response.text();
          let errorMessage = `HTTP ${response.status}`;
          try {
            const errorJson = JSON.parse(errorText);
            errorMessage = errorJson.detail || errorJson.message || errorMessage;
          } catch {
            errorMessage = errorText || errorMessage;
          }
          
          // 🐛 Aggiungi errore al debug panel
          setCrawlDebug(prev => ({
            ...prev,
            errors: [...prev.errors, `ERRORE HTTP ${response.status}: ${errorMessage}`],
            last_update: Date.now()
          }));
          
          throw new Error(errorMessage);
        }

        const result = await response.json();
        
        // 🐛 Aggiorna debug panel con risultati Fast Mode
        if (result.pages_crawled && result.pages_crawled > 0) {
          setCrawlDebug(prev => ({
            ...prev,
            urls_processed: Array.from({ length: result.pages_crawled }, (_, i) => `Page ${i + 1}`),
            total_chunks: result.chunks_created || 0,
            last_update: Date.now()
          }));
        } else {
          // ⚠️ Nessuna pagina processata - aggiungi warning
          setCrawlDebug(prev => ({
            ...prev,
            errors: [...prev.errors, `⚠️ ATTENZIONE: 0 pagine processate! Verifica URL e dominio filter.`],
            last_update: Date.now()
          }));
        }
        
        setCrawlLog(prev => [
          ...prev,
          `✅ Fast Crawl e Indicizzazione completati!`,
          `🕷️ Modalità: ${result.crawler_used || 'scrapy'}`,
          `📄 Pagine crawlate: ${result.pages_crawled ?? result.stats?.crawled ?? 0}`,
          `📦 Chunks creati: ${result.chunks_created || 0}`,
          `⏱️ Tempo crawling: ${(result.crawling_duration ?? result.stats?.duration ?? '?')}s`,
          `⏱️ Tempo indicizzazione: ${result.indexing_duration ?? 0}s`,
          `⏱️ Tempo totale: ${(result.total_duration ?? '?')}s`,
          `🗂️ Indice: ${result.index_name || 'N/A'}`,
          result.chunks_created === 0 ? `⚠️ ATTENZIONE: 0 chunks creati! Verifica URL e contenuto.` : `✅ Fast Mode ora include anche indicizzazione automatica!`
        ]);
        
        setCrawlProgress({ 
          ...crawlProgress, 
          phase: 'completed',
          chunks_created: result.chunks_created || 0,
          pages_processed: result.pages_crawled || 0
        });
        
        // ⚠️ Alert più dettagliato se 0 chunks
        if (result.chunks_created === 0) {
          alert(`⚠️ ATTENZIONE: Fast Crawl completato ma 0 chunks creati!\n\n📄 Pagine crawlate: ${result.pages_crawled || 0}\n📦 Chunks creati: 0\n🗂️ Indice: ${result.index_name || 'N/A'}\n\n💡 Possibili cause:\n- URL non raggiungibile\n- Dominio filter troppo restrittivo\n- Pagine senza contenuto testuale\n- Errore durante indicizzazione\n\n🐛 Controlla il pannello DEBUG per dettagli`);
        } else {
          // Messaggio aggiornato: Fast Crawler ora fa anche indicizzazione
          alert(`✅ Fast Crawl e Indicizzazione completati!\n\n📄 Pagine crawlate: ${result.pages_crawled ?? result.stats?.crawled ?? 0}\n📦 Chunks creati: ${result.chunks_created ?? 0}\n⏱️ Tempo crawling: ${result.crawling_duration ?? result.stats?.duration ?? '?'}s\n⏱️ Tempo indicizzazione: ${result.indexing_duration ?? 0}s\n⏱️ Tempo totale: ${result.total_duration ?? '?'}s\n🗂️ Indice: ${result.index_name ?? 'N/A'}\n\n✅ Fast Mode ora include anche indicizzazione automatica!`);
        }
        
        await loadIndices();
        if (crawlHeartbeatRef.current) {
          clearInterval(crawlHeartbeatRef.current);
          crawlHeartbeatRef.current = null;
        }
        return;
      }

      // Crawling normale con streaming
      const params = {
        seeds: crawlUrl.split('\n').map(url => url.trim()).filter(url => url),
        depth: crawlDepth,
        download_pdfs: downloadPdfs,
        embed_model: embedModel,
        index_suffix: crawlIndexSuffix,
        domain_filter: domainFilter, // 🔒 Dominio da seguire (es. www.marinuzzi.it)
        allow_external_sites: allowExternalSites, // 🌐 Permetti siti terzi
        crawl_focus_keywords: focusKeywords ? focusKeywords.split(',').map(k => k.trim()).filter(k => k) : [],
        use_semantic_chunking: useSemanticChunking,
        similarity_threshold: similarityThreshold,
        min_chunk_chars: minChunkChars,
        max_chunk_chars: maxChunkChars,
        chunk_size: chunkSize,
        chunk_overlap: chunkOverlap,
        batch_size: crawlerBatchSize,
        download_only_pdfs: downloadOnlyPdfs,
        download_images: downloadImages,
        enable_ocr: enableOcrImages,
        enable_pdf_ocr: enableOcrPdfs,
        enable_javascript: enableJavaScript,
      };

      // 🔍 Verifica backend disponibile prima di iniziare
      setCrawlLog(prev => [...prev, '🔍 Verifica connessione backend...']);
      try {
        const healthCheck = await fetch(`${API_URL}/health`, { method: 'GET' });
        if (!healthCheck.ok) {
          throw new Error('Backend non raggiungibile');
        }
      } catch (e) {
        setCrawlLog(prev => [...prev, '⚠️ Backend non raggiungibile, provo comunque...']);
      }

      setCrawlLog(prev => [...prev, '📡 Invio richiesta crawling...']);
      const response = await fetch(`${API_URL}/api/crawl?background=false`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `HTTP ${response.status}`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.detail || errorJson.message || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('❌ Nessuna risposta dal server. Verifica che il backend sia attivo.');
      }

      setCrawlLog(prev => [...prev, '✅ Connessione stabilita, inizio streaming...']);
      const decoder = new TextDecoder();
      let buffer = '';
      let lastUpdate = Date.now();
      const TIMEOUT_MS = 300000; // 5 minuti timeout
      let hasReceivedData = false; // 🐛 Traccia se abbiamo ricevuto almeno un chunk

      while (true) {
        // Timeout check
        if (Date.now() - lastUpdate > TIMEOUT_MS) {
          throw new Error('⏱️ Timeout: Nessun aggiornamento ricevuto per 5 minuti. Il crawling potrebbe essere bloccato.');
        }

        const { done, value } = await reader.read();
        if (done) {
          if (!hasReceivedData) {
            // Se non abbiamo ricevuto dati, potrebbe essere un errore silente
            setCrawlLog(prev => [...prev, '⚠️ Streaming completato ma nessun dato ricevuto. Verifica che il KB backend sia attivo.']);
            setCrawlDebug(prev => ({
              ...prev,
              errors: [...prev.errors, 'Streaming completato senza dati - KB backend potrebbe non essere attivo'],
              last_update: Date.now()
            }));
          } else {
            setCrawlLog(prev => [...prev, '✅ Streaming completato']);
          }
          break;
        }
        
        hasReceivedData = true; // Abbiamo ricevuto almeno un chunk
        
        lastUpdate = Date.now();

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.substring(6);
            try {
              const parsed = JSON.parse(data);
              
              // Aggiorna log
              if (parsed.msg) {
                setCrawlLog(prev => [...prev, parsed.msg]);
              }
              
              // 🐛 Aggiorna debug panel
              setCrawlDebug(prev => {
                const updated = { ...prev, last_update: Date.now() };
                
                // URL visitato
                if (parsed.current_url && !prev.urls_visited.includes(parsed.current_url)) {
                  updated.urls_visited = [...prev.urls_visited, parsed.current_url];
                }
                
                // URL processato
                if (parsed.url_processed && !prev.urls_processed.includes(parsed.url_processed)) {
                  updated.urls_processed = [...prev.urls_processed, parsed.url_processed];
                  if (parsed.chunks_created) {
                    updated.chunks_by_url[parsed.url_processed] = parsed.chunks_created;
                    updated.total_chunks += parsed.chunks_created;
                  }
                }
                
                // URL fallito
                if (parsed.url_failed) {
                  updated.urls_failed = [...prev.urls_failed, {
                    url: parsed.url_failed,
                    error: parsed.error || 'Errore sconosciuto'
                  }];
                }
                
                // Errori - gestisci sia parsed.error che parsed.msg se contiene errori
                if (parsed.error) {
                  const errorMsg = typeof parsed.error === 'string' ? parsed.error : JSON.stringify(parsed.error);
                  if (!prev.errors.includes(errorMsg)) {
                    updated.errors = [...prev.errors, errorMsg];
                  }
                }
                
                // Se il messaggio contiene un errore, aggiungilo
                if (parsed.msg && (parsed.msg.includes('❌') || parsed.msg.includes('⚠️') || parsed.msg.includes('Errore'))) {
                  if (!prev.errors.includes(parsed.msg)) {
                    updated.errors = [...prev.errors, parsed.msg];
                  }
                }
                
                // URL in coda (se disponibile) + delta per trasparenza
                if (parsed.urls_in_queue) {
                  updated.queue_delta = parsed.urls_in_queue.length - (prev.urls_in_queue?.length ?? 0);
                  updated.urls_in_queue = parsed.urls_in_queue;
                }
                
                return updated;
              });
              
              // Aggiorna progress
              if (parsed.pages_processed !== undefined) {
                setCrawlProgress(prev => ({ ...prev, pages_processed: parsed.pages_processed }));
              }
              if (parsed.current_url) {
                setCrawlProgress(prev => ({ ...prev, current_url: parsed.current_url }));
              }
              if (parsed.chunks_created !== undefined) {
                setCrawlProgress(prev => ({ ...prev, chunks_created: parsed.chunks_created }));
              }
            } catch (e) {
              console.warn('Failed to parse SSE data:', data);
              setCrawlDebug(prev => ({
                ...prev,
                errors: [...prev.errors, `Errore parsing: ${data.substring(0, 100)}`]
              }));
            }
          } else if (line.startsWith('event: index_created')) {
            setCrawlLog(prev => [...prev, '✅ Indice creato con successo!']);
            setCrawlProgress(prev => ({ ...prev, phase: 'completed' }));
          }
        }
      }

      // Verifica se abbiamo ricevuto dati
      if (!hasReceivedData) {
        console.error('❌ Nessun dato ricevuto dallo stream SSE');
        setCrawlLog(prev => [...prev, '❌ ERRORE: Nessun dato ricevuto dal backend', '💡 Possibili cause:', '- Backend RAG (porta 8002) non raggiungibile', '- Errore durante avvio crawling', '- Timeout connessione']);
        setCrawlProgress(prev => ({ ...prev, phase: 'error' }));
        setCrawlDebug(prev => ({
          ...prev,
          errors: [...prev.errors, 'ERRORE CRITICO: Nessun dato ricevuto dallo stream SSE. Backend potrebbe non essere raggiungibile.'],
          last_update: Date.now()
        }));
        alert(`❌ ERRORE: Backend non ha inviato dati!\n\n💡 Verifica:\n- Backend RAG (porta 8002) è attivo?\n- URL valido e raggiungibile?\n- Console browser per dettagli\n\n🐛 Controlla il pannello DEBUG per dettagli`);
        setIsCrawling(false);
        return;
      }
      
      // NON PULIRE I CAMPI - mantieni valori per crawling multipli!
      setCrawlLog(prev => [...prev, '✅ Crawling completato con successo!']);
      setCrawlProgress(prev => ({ ...prev, phase: 'completed' }));
      await loadIndices();
      
      // Mostra risultato - usa i valori aggiornati dal debug panel
      const finalPages = crawlDebug.urls_processed.length || crawlProgress.pages_processed || 0;
      const finalChunks = crawlDebug.total_chunks || crawlProgress.chunks_created || 0;
      
      // ⚠️ Alert più dettagliato se 0 chunks
      if (finalChunks === 0) {
        alert(`⚠️ ATTENZIONE: Crawling completato ma 0 chunks creati!\n\n📄 Pagine processate: ${finalPages}\n📦 Chunks creati: 0\n🗂️ Indice: ${webIndexName}\n\n💡 Possibili cause:\n- URL non raggiungibile\n- Dominio filter troppo restrittivo\n- Pagine senza contenuto testuale\n- Errore durante indicizzazione\n\n🐛 Controlla il pannello DEBUG per dettagli`);
      } else {
        alert(`✅ Crawling completato!\n\n📄 Pagine processate: ${finalPages}\n📦 Chunks creati: ${finalChunks}\n🗂️ Indice: ${webIndexName}`);
      }
    } catch (error: any) {
      console.error('❌ Errore nel crawling:', error);
      const errorMsg = error.message || 'Errore sconosciuto';
      setCrawlLog(prev => [...prev, `❌ Errore: ${errorMsg}`, '💡 Suggerimenti:', '- Verifica che il backend RAG (porta 8002) sia attivo', '- Controlla la console per dettagli', '- Prova con un URL più semplice']);
      setCrawlProgress(prev => ({ ...prev, phase: 'error' }));
      
      // 🐛 Aggiungi errore al debug panel
      setCrawlDebug(prev => ({
        ...prev,
        errors: [...prev.errors, `ERRORE CRITICO: ${errorMsg}`],
        last_update: Date.now()
      }));
      
      // Messaggio errore dettagliato
      alert(`❌ Errore durante il crawling:\n\n${errorMsg}\n\n💡 Verifica:\n- Backend RAG attivo (porta 8002)\n- URL valido e raggiungibile\n- Console browser per dettagli\n\n🐛 Controlla il pannello DEBUG per dettagli`);
    } finally {
      if (crawlHeartbeatRef.current) {
        clearInterval(crawlHeartbeatRef.current);
        crawlHeartbeatRef.current = null;
      }
      setIsCrawling(false);
      if (crawlProgress.phase !== 'error') {
        setCrawlProgress(prev => ({ ...prev, phase: 'idle' }));
      }
    }
  };
  
  // Funzione per pulire i campi manualmente
  const clearCrawlerFields = () => {
    setCrawlUrl('');
    setWebIndexName('');
    setCrawlDepth(2);
    // Non c'è uno stato per maxPages - viene gestito inline
    setCrawlLog([]);
    setCrawlProgress({ phase: 'idle', pages_processed: 0, chunks_created: 0, current_url: '' });
  };

  // ⚡ Suggerimento modalità: analizza il sito e propone Fast Mode / Playwright / Attiva JS
  const handleSuggestNavigation = async () => {
    const urls = crawlUrl.split('\n').map(u => u.trim()).filter(Boolean);
    const firstUrl = urls[0];
    if (!firstUrl) {
      alert('Inserisci almeno un URL da crawlare, poi clicca Suggerimento.');
      return;
    }
    try {
      new URL(firstUrl);
    } catch {
      alert('URL non valido. Inserisci un URL completo (es. https://www.esempio.it)');
      return;
    }
    setIsDetectingSuggestion(true);
    setSuggestionMessage(null);
    try {
      const res = await fetch(`${API_URL}/api/crawl/detect?url=${encodeURIComponent(firstUrl)}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Errore analisi sito');
      const rec = data.recommended_crawler;
      const reasons = data.reasons || [];
      if (rec === 'scrapy') {
        setUseFastMode(true);
        setUsePlaywright(false);
        setEnableJavaScript(false);
        setSuggestionMessage(`🚀 Scrapy (10x veloce): ${reasons.join('. ')}`);
      } else {
        setUseFastMode(false);
        setUsePlaywright(true);
        setEnableJavaScript(true);
        setSuggestionMessage(`🎭 Playwright (JS): ${reasons.join('. ')}`);
      }
    } catch (e) {
      setSuggestionMessage(`⚠️ Analisi non disponibile: ${String(e)}`);
    } finally {
      setIsDetectingSuggestion(false);
    }
  };

  // 🚀 Funzione per avviare crawling in BACKGROUND (senza streaming)
  const handleCrawlBackground = async () => {
    // ✅ VALIDAZIONE PARAMETRI (stessa logica di handleCrawlWithStreaming)
    if (!crawlUrl.trim() || !webIndexName.trim()) {
      alert('❌ Inserisci URL e nome indice!');
      return;
    }

    // Valida URL
    const urls = crawlUrl.split('\n').map(url => url.trim()).filter(url => url);
    const invalidUrls = urls.filter(url => {
      try {
        new URL(url);
        return false;
      } catch {
        return true;
      }
    });

    if (invalidUrls.length > 0) {
      alert(`❌ URL non validi:\n${invalidUrls.join('\n')}\n\nInserisci URL completi (es. https://example.com)`);
      return;
    }

    // Valida nome indice
    if (!/^[a-zA-Z0-9_-]+$/.test(webIndexName.trim())) {
      alert('❌ Nome indice non valido!\n\nUsa solo lettere, numeri, underscore (_) e trattini (-)\nEs: "mio-indice-2024"');
      return;
    }

    setShowLogPanel(true); // Trasparenza: apri il Log anche per crawl in background
    try {
      // 🔍 Verifica backend disponibile
      try {
        const healthCheck = await fetch(`${API_URL}/health`, { method: 'GET' });
        if (!healthCheck.ok) {
          throw new Error('Backend non raggiungibile');
        }
      } catch (e) {
        console.warn('⚠️ Health check fallito, continuo comunque...');
      }
      const params = {
        seeds: crawlUrl.split('\n').map(url => url.trim()).filter(url => url),
        depth: crawlDepth,
        download_pdfs: downloadPdfs,
        embed_model: embedModel,
        index_suffix: crawlIndexSuffix,
        domain_filter: domainFilter, // 🔒 Dominio da seguire (es. www.marinuzzi.it)
        allow_external_sites: allowExternalSites, // 🌐 Permetti siti terzi
        crawl_focus_keywords: focusKeywords ? focusKeywords.split(',').map(k => k.trim()).filter(k => k) : [],
        use_semantic_chunking: useSemanticChunking,
        similarity_threshold: similarityThreshold,
        min_chunk_chars: minChunkChars,
        max_chunk_chars: maxChunkChars,
        chunk_size: chunkSize,
        chunk_overlap: chunkOverlap,
        batch_size: crawlerBatchSize,
        download_only_pdfs: downloadOnlyPdfs,
        download_images: downloadImages,
        enable_ocr: enableOcrImages,
        enable_pdf_ocr: enableOcrPdfs,
        enable_javascript: enableJavaScript,
      };

      const response = await fetch(`${API_URL}/api/crawl?background=true`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `HTTP ${response.status}`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.detail || errorJson.message || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      
      // NON pulire i campi - mantieni per crawling multipli
      // setCrawlUrl('');
      // setWebIndexName('');
      
      if (result.crawl_id) {
        alert(`✅ Crawling avviato in background!\n\n🆔 ID: ${result.crawl_id}\n🌐 URL: ${urls[0]}\n🗂️ Indice: ${webIndexName}\n\n💡 Il crawling continuerà anche se chiudi questa pagina.\n\nPuoi monitorare lo stato nella sezione "Crawling Attivi".`);
        await loadActiveCrawls(); // Ricarica crawling attivi
      } else {
        alert(`✅ Richiesta inviata!\n\n🗂️ Indice: ${webIndexName}\n\n💡 Verifica lo stato nella sezione "Crawling Attivi".`);
      }
      await loadIndices();
    } catch (error: any) {
      console.error('❌ Errore nel crawling background:', error);
      const errorMsg = error.message || 'Errore sconosciuto';
      alert(`❌ Errore durante l'avvio del crawling:\n\n${errorMsg}\n\n💡 Verifica:\n- Backend RAG attivo (porta 8002)\n- URL valido e raggiungibile\n- Console browser per dettagli`);
    }
  };


  const handleInspectIndex = async (indexId: string, page: number = 1) => {
    try {
      const response = await fetch(`${API_URL}/api/indices/${indexId}/details?page=${page}&limit=500`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      
      console.log('📊 Index details received:', data);
      
      setInspectData(data);
      setShowInspectModal(true);
    } catch (error: any) {
      console.error('Errore dettagli indice:', error);
      alert(`❌ Errore caricamento dettagli: ${error.message}`);
    }
  };

  const handleDeleteIndex = async (indexId: string) => {
    if (!confirm('⚠️ Eliminare questo indice?')) return;

    try {
      const response = await fetch(`${API_URL}/api/flow/kb/delete-index/${indexId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        alert('✅ Indice eliminato!');
        loadIndices();
      }
    } catch (error) {
      alert('❌ Errore eliminazione indice');
    }
  };

  return (
    <div className="flex-1 bg-gradient-to-br from-blue-50 to-indigo-50 overflow-auto">
      <div className="p-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">📚 Knowledge Base</h1>
              <p className="text-gray-600">
                Carica documenti PDF o indicizza siti web per utilizzarli nei tuoi flussi conversazionali.
              </p>
            </div>
            {/* 🔌 Badge stato connessione */}
            <div className="flex items-center gap-2">
              {backendStatus === 'checking' && (
                <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg border border-gray-300">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse"></div>
                  <span className="text-xs font-medium text-gray-600">Verifica...</span>
                </div>
              )}
              {backendStatus === 'online' && (
                <div className="flex items-center gap-2 px-3 py-2 bg-green-50 rounded-lg border border-green-300">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-xs font-medium text-green-700">Backend Online</span>
                </div>
              )}
              {backendStatus === 'offline' && (
                <div className="flex items-center gap-2 px-3 py-2 bg-red-50 rounded-lg border border-red-300">
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  <span className="text-xs font-medium text-red-700">Backend Offline</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* CODA DISABILITATA - Sistema semplice con processing diretto */}
        {/* 🗑️ RIMOSSO - Sistema coda UI non più attivo */}
        {/* {false && (activeJob || queueJobs.length > 0) && (...)} */}

        {/* ❌ VECCHIA SEZIONE "Carica PDF" RIMOSSA - Ora gestita dal sistema a 2 fasi sotto */}

        {/* 📦 PROCESSI IN BACKGROUND (PDF indicizzazione + Crawling) */}
        {(backgroundPdfJobs.length > 0 || activeCrawls.length > 0) && (
          <div className="bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-300 rounded-xl shadow-md p-6 mb-6">
            {(() => {
              const hasRunningPdf = backgroundPdfJobs.some((j: any) => j.status === 'running');
              const hasRunningCrawl = activeCrawls.some((c: any) => c.status === 'running');
              const hasAnyRunning = hasRunningPdf || hasRunningCrawl;
              return (
            <button
              type="button"
              onClick={() => setShowBackgroundPanel(!showBackgroundPanel)}
              className="flex items-center justify-between w-full mb-4 text-left"
            >
              <div className="flex items-center">
                {hasAnyRunning ? (
                  <Loader className="w-6 h-6 text-slate-600 mr-2 animate-spin" />
                ) : (
                  <FileText className="w-6 h-6 text-slate-500 mr-2" />
                )}
                <h2 className="text-xl font-bold text-gray-900">
                  📦 Processi in background ({backgroundPdfJobs.length + activeCrawls.length})
                </h2>
              </div>
              {showBackgroundPanel ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
              );
            })()}

            {showBackgroundPanel && (
              <div>
              <div className="space-y-6">
                {/* Indicizzazione PDF */}
                {backgroundPdfJobs.length > 0 && (
                  <div>
                    <h3 className="text-sm font-bold text-gray-700 mb-2">📄 Indicizzazione PDF</h3>
                    <div className="space-y-2">
                      {backgroundPdfJobs.map((job: any) => {
                        const progress = job.files_count > 0 
                          ? Math.round(((job.processed_count ?? 0) / job.files_count) * 100) 
                          : 0;
                        
                        return (
                        <div key={job.id} className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm">
                          <div className="flex items-center justify-between gap-2 mb-3">
                            <div className="flex-1 min-w-0">
                              <span className="font-mono text-sm text-gray-600">{job.id}</span>
                              <span className="mx-2">·</span>
                              <span className="font-medium text-gray-900">{job.index_name}</span>
                              <span className={`ml-2 px-2 py-0.5 text-xs font-bold rounded ${
                                job.status === 'running' ? 'bg-amber-100 text-amber-800' :
                                job.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {job.status === 'running' ? '⏳ In corso' : job.status === 'completed' ? '✅ Completato' : '❌ Fallito'}
                              </span>
                            </div>
                            <div className="text-sm text-gray-600 whitespace-nowrap">
                              {job.processed_count ?? 0}/{job.files_count} file ({progress}%)
                              {job.created_at && (
                                <span className="ml-2 text-gray-400">
                                  {new Date(job.created_at * 1000).toLocaleString()}
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {/* Barra di progresso */}
                          {job.status === 'running' && (
                            <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2 overflow-hidden">
                              <div 
                                className="bg-gradient-to-r from-blue-500 to-green-500 h-2.5 rounded-full transition-all duration-500 ease-out"
                                style={{ width: `${progress}%` }}
                              >
                                {progress === 0 && (
                                  <div className="w-full h-full bg-gradient-to-r from-blue-400 via-blue-500 to-blue-400 animate-pulse" />
                                )}
                              </div>
                            </div>
                          )}
                          
                          {job.error && (
                            <p className="mt-2 text-xs text-red-600">{job.error}</p>
                          )}
                        </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Crawling Attivi */}
                {activeCrawls.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-bold text-gray-700">🔄 Crawling Attivi</h3>
                      {activeCrawls.length > 1 && (
                        <button
                          onClick={stopAllCrawls}
                          className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-sm font-bold rounded-lg"
                        >
                          🛑 Ferma Tutti
                        </button>
                      )}
                    </div>
                    <div className="space-y-3">
                      {activeCrawls.map((crawl: any) => (
                <div key={crawl.crawl_id} className="bg-white rounded-lg p-4 border-2 border-orange-200 shadow-sm">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-sm text-gray-500">{crawl.crawl_id}</span>
                        <span className={`px-2 py-0.5 text-xs font-bold rounded ${
                          crawl.status === 'running' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {crawl.status === 'running' ? '🟢 Running' : crawl.status}
                        </span>
                      </div>
                      
                      <div className="text-sm text-gray-700 mb-2">
                        <strong>Seeds:</strong> {crawl.config?.seeds?.join(', ') || 'N/A'}
                      </div>
                      
                      {crawl.progress?.current_url && (
                        <div className="text-xs text-blue-600 truncate mb-2">
                          📄 {crawl.progress.current_url}
                        </div>
                      )}
                      
                      <div className="flex gap-4 text-sm">
                        <span className="text-green-700">
                          <strong>Pagine:</strong> {crawl.progress?.pages_processed || 0}
                        </span>
                        <span className="text-blue-700">
                          <strong>Chunks:</strong> {crawl.progress?.chunks_created || 0}
                        </span>
                        <span className="text-gray-600">
                          <strong>Depth:</strong> {crawl.config?.depth || '?'}
                        </span>
                      </div>
                    </div>
                    
                    <button
                      onClick={() => stopCrawl(crawl.crawl_id)}
                      className="ml-4 px-3 py-2 bg-red-500 hover:bg-red-600 text-white font-bold rounded transition-colors flex items-center gap-1"
                      title="Ferma questo crawling"
                    >
                      <span>🛑</span>
                      Stop
                    </button>
                  </div>
                  
                  {crawl.progress?.errors && crawl.progress.errors.length > 0 && (
                    <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                      <strong>⚠️ Errori:</strong> {crawl.progress.errors.join(', ')}
                    </div>
                  )}
                </div>
              ))}
            </div>
                  </div>
                )}

                {/* Pulsanti purga processi in background */}
                {(backgroundPdfJobs.length > 0) && (
                  <div className="mt-4 flex flex-wrap gap-2 justify-center">
                    <button
                      type="button"
                      onClick={() => purgeBackgroundJobs(true)}
                      className="px-3 py-2 bg-amber-100 hover:bg-amber-200 text-amber-800 text-sm font-medium rounded-lg border border-amber-300"
                      title="Rimuovi solo job completati o falliti"
                    >
                      🧹 Pulisci completati/falliti
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (confirm('Vuoi svuotare tutta la lista dei processi in background?')) {
                          purgeBackgroundJobs(false);
                        }
                      }}
                      className="px-3 py-2 bg-red-100 hover:bg-red-200 text-red-800 text-sm font-medium rounded-lg border border-red-300"
                      title="Rimuovi tutti i job (anche in corso)"
                    >
                      🗑️ Pulisci tutto
                    </button>
                  </div>
                )}

                <div className="mt-4 text-xs text-gray-500 text-center">
                  ℹ️ Aggiornamento automatico ogni 10 secondi
                </div>
              </div>
              </div>
            )}
          </div>
        )}

        {/* 📄 SEZIONE PDF - Raggruppa tutte le operazioni PDF */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6 border border-gray-200">
          <button
            type="button"
            onClick={() => setShowPdfSection(!showPdfSection)}
            className="flex items-center justify-between w-full text-left"
          >
            <div className="flex items-center">
              <FileText className="w-6 h-6 text-blue-500 mr-2" />
              <h2 className="text-xl font-bold text-gray-900">📄 Gestione Documenti</h2>
            </div>
            {showPdfSection ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          
          <div className={showPdfSection ? "mt-4 space-y-6" : "hidden"}>

        {/* 📤 SEZIONE 1: CARICA PDF (senza indicizzazione) */}
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div className="flex items-center mb-4">
            <FileText className="w-5 h-5 text-blue-500 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">📤 Carica File sul Server</h3>
          </div>
          
          <p className="text-sm text-gray-600 mb-4">
            Carica i tuoi file (PDF, TXT, MD) sul server. <strong>Nessuna indicizzazione</strong> avviene in questa fase.
          </p>

          {/* Pulsanti per selezione file/cartella */}
          <div className="flex gap-2 mb-3">
            <button
              type="button"
              onClick={() => {
                if (!isUploadingPDF && fileInputRef.current) {
                  fileInputRef.current.removeAttribute('webkitdirectory');
                  fileInputRef.current.click();
                }
              }}
              disabled={isUploadingPDF}
              className="flex-1 px-4 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
            >
              📄 Scegli File
            </button>
            <button
              type="button"
              onClick={() => {
                if (!isUploadingPDF && fileInputRef.current) {
                  fileInputRef.current.setAttribute('webkitdirectory', '');
                  fileInputRef.current.click();
                }
              }}
              disabled={isUploadingPDF}
              className="flex-1 px-4 py-2 text-sm bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
            >
              📁 Scegli Cartella
            </button>
          </div>

          <div 
            className={`relative min-h-[120px] flex items-center justify-center border-2 border-dashed rounded-lg transition-all ${
              isDragging 
                ? 'border-blue-500 bg-blue-50' 
                : 'border-gray-300 hover:border-blue-400 bg-gray-50'
            } ${isUploadingPDF ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!isUploadingPDF) setIsDragging(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsDragging(false);
            }}
            onDrop={async (e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsDragging(false);
              
              if (isUploadingPDF) return;
              
              console.log('📦 Drag & Drop event ricevuto');
              
              try {
                // 🗂️ Usa DataTransferItem API per supportare cartelle
                if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
                  console.log('📦 Usando DataTransferItem API (supporta cartelle)');
                  
                  const allFiles: File[] = [];
                  
                  // Processa ogni item (può essere file o cartella)
                  for (let i = 0; i < e.dataTransfer.items.length; i++) {
                    const item = e.dataTransfer.items[i];
                    
                    if (item.kind === 'file') {
                      const entry = item.webkitGetAsEntry();
                      
                      if (entry) {
                        console.log(`📂 Processando: ${entry.name} (${entry.isDirectory ? 'cartella' : 'file'})`);
                        
                        // Leggi ricorsivamente (funziona sia per file che cartelle)
                        const files = await readEntriesRecursively(entry);
                        allFiles.push(...files);
                      }
                    }
                  }
                  
                  console.log(`✅ File totali trovati: ${allFiles.length}`);
                  
                  if (allFiles.length === 0) {
                    alert('❌ Nessun file supportato trovato (PDF, TXT, MD)');
                    return;
                  }
                  
                  setPendingFiles(allFiles);
                  alert(`✅ ${allFiles.length} file pronti per essere caricati!`);
                  
                } else if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                  // Fallback: browser vecchi senza DataTransferItem
                  console.log('📦 Usando DataTransfer.files (fallback, no cartelle)');
                  
                  const allFiles = Array.from(e.dataTransfer.files);
                  const supported = allFiles.filter(file => {
                    const ext = file.name.toLowerCase();
                    return file.type === 'application/pdf' || ext.endsWith('.pdf') || ext.endsWith('.txt') || ext.endsWith('.md');
                  });
                  
                  console.log(`✅ File supportati: ${supported.length}/${allFiles.length}`);
                  
                  if (supported.length === 0) {
                    alert('❌ Nessun file supportato trovato (PDF, TXT, MD)');
                    return;
                  }
                  
                  setPendingFiles(supported);
                  alert(`✅ ${supported.length} file pronti per essere caricati!`);
                }
              } catch (error) {
                console.error('❌ Errore lettura file:', error);
                alert('❌ Errore durante la lettura dei file dalla cartella');
              }
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.txt,.md"
              multiple
              onChange={(e) => {
                if (e.target.files && e.target.files.length > 0) {
                  const allFiles = Array.from(e.target.files);
                  const supported = allFiles.filter(file => {
                    const ext = file.name.toLowerCase();
                    return file.type === 'application/pdf' || ext.endsWith('.pdf') || ext.endsWith('.txt') || ext.endsWith('.md');
                  });
                  
                  if (supported.length === 0) {
                    alert('❌ Nessun file supportato trovato (PDF, TXT, MD)');
                    return;
                  }
                  
                  setPendingFiles(supported);
                }
              }}
              disabled={isUploadingPDF}
              className="hidden"
            />
            <div className="text-center p-6">
              <FileText className={`w-12 h-12 mx-auto mb-3 ${isDragging ? 'text-blue-500' : 'text-gray-400'}`} />
              <p className={`text-sm font-medium ${isDragging ? 'text-blue-600' : 'text-gray-600'}`}>
                {isDragging ? 'Rilascia file o cartella qui' : 'Trascina file o cartella qui (PDF, TXT, MD)'}
              </p>
              <p className="text-xs text-gray-400 mt-1">oppure usa i pulsanti sopra</p>
            </div>
          </div>
          
          {pendingFiles.length > 0 && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm font-medium text-blue-900 mb-2">
                📋 {pendingFiles.length} file pronti per il caricamento:
              </p>
              <div className="max-h-32 overflow-y-auto text-xs text-blue-800 space-y-1">
                {pendingFiles.slice(0, 10).map((file, idx) => (
                  <div key={idx} className="flex justify-between">
                    <span className="truncate">{file.name}</span>
                    <span className="ml-2 text-blue-600 flex-shrink-0">
                      {(file.size / (1024 * 1024)).toFixed(2)} MB
                    </span>
                  </div>
                ))}
                {pendingFiles.length > 10 && (
                  <p className="text-blue-600 italic">...e altri {pendingFiles.length - 10} file</p>
                )}
              </div>
              
              <button
                onClick={handleUploadOnly}
                disabled={isUploadingPDF}
                className="w-full mt-3 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isUploadingPDF ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin mr-2" />
                    Caricamento in corso...
                  </>
                ) : (
                  <>
                    <FileText className="w-5 h-5 mr-2" />
                    Carica {pendingFiles.length} file sul Server
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* 📋 SEZIONE 2: PDF CARICATI SUL SERVER */}
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <FileText className="w-5 h-5 text-indigo-500 mr-2" />
              <h3 className="text-lg font-semibold text-gray-900">📋 PDF Caricati sul Server</h3>
            </div>
            <button
              onClick={loadUploadedPdfs}
              disabled={isLoadingPdfs}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium disabled:text-gray-400"
            >
              {isLoadingPdfs ? '⏳' : '🔄'} Aggiorna
            </button>
          </div>
          
          <p className="text-sm text-gray-600 mb-4">
            Seleziona i file da indicizzare. Puoi scegliere uno o più file per creare un indice.
          </p>

          {/* Pulsanti azioni */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={handleSelectAll}
              disabled={uploadedPdfs.length === 0}
              className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
            >
              ✅ Seleziona Tutti
            </button>
            <button
              onClick={handleDeselectAll}
              disabled={selectedPdfFiles.size === 0}
              className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-300 disabled:cursor-not-allowed"
            >
              ❌ Deseleziona
            </button>
            <button
              onClick={handleDeleteSelected}
              disabled={selectedPdfFiles.size === 0}
              className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
            >
              🗑️ Elimina ({selectedPdfFiles.size})
            </button>
          </div>

          {/* Lista PDF con checkbox */}
          {isLoadingPdfs ? (
            <div className="flex items-center justify-center py-8 text-gray-500">
              <Loader className="w-5 h-5 animate-spin mr-2" />
              Caricamento...
            </div>
          ) : uploadedPdfs.length === 0 ? (
            <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
              <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p>Nessun file caricato</p>
              <p className="text-sm">Usa la sezione "Carica File" sopra</p>
            </div>
          ) : (
            <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
              {uploadedPdfs.map((pdf) => (
                <div
                  key={pdf.filename}
                  className="flex items-center p-3 hover:bg-gray-50 border-b border-gray-100 last:border-0 cursor-pointer"
                  onClick={() => togglePdfSelection(pdf.filename)}
                >
                  <input
                    type="checkbox"
                    checked={selectedPdfFiles.has(pdf.filename)}
                    onChange={() => togglePdfSelection(pdf.filename)}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-3"
                  />
                  <FileText className="w-5 h-5 text-red-500 mr-2 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{pdf.filename}</p>
                    <p className="text-xs text-gray-500">{pdf.size_mb} MB</p>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          <div className="mt-2 text-sm text-gray-600">
            📊 {uploadedPdfs.length} PDF disponibili • {selectedPdfFiles.size} selezionati
          </div>
        </div>

        {/* 🔍 SEZIONE 2.5: CONTROLLO PDF E ANTEPRIMA (espandibile) */}
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <button
            type="button"
            onClick={() => setShowPdfPreviewSection(!showPdfPreviewSection)}
            className="flex items-center justify-between w-full text-left"
          >
            <div className="flex items-center">
              <BookOpen className="w-5 h-5 text-purple-500 mr-2" />
              <h3 className="text-lg font-semibold text-gray-900">🔍 Controllo PDF e Anteprima</h3>
            </div>
            {showPdfPreviewSection ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          
          <div className={showPdfPreviewSection ? "" : "hidden"}>
          <p className="text-sm text-gray-600 mb-4">
            Seleziona un PDF caricato per generare un'anteprima dei chunks o eseguire un'analisi avanzata del preprocessing.
          </p>

          {/* Controlli */}
          <div className="flex gap-4 mb-4">
            {/* Select PDF */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                📄 PDF da analizzare:
              </label>
              <select
                value={selectedPdfForPreview}
                onChange={(e) => setSelectedPdfForPreview(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="">Seleziona un PDF...</option>
                {uploadedPdfs.map((pdf) => (
                  <option key={pdf.filename} value={pdf.filename}>
                    {pdf.filename} ({pdf.size_mb} MB)
                  </option>
                ))}
              </select>
            </div>

            {/* Numero chunks - PIÙ STRETTO */}
            <div className="w-32">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                📊 Chunks:
              </label>
              <input
                type="number"
                value={displayCountPreview}
                onChange={(e) => setDisplayCountPreview(parseInt(e.target.value) || 3)}
                min="1"
                max="20"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
            </div>

            {/* Bottoni - Allineati a destra */}
            <div className="flex items-end gap-2">
              <button
                onClick={handleGeneratePreview}
                disabled={!selectedPdfForPreview || isGeneratingPreview}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-4 rounded-lg shadow-sm text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
              >
                {isGeneratingPreview ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin mr-2" />
                    ...
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    🔍 Anteprima
                  </>
                )}
              </button>

              <button
                onClick={handleGenerateAdvancedPreview}
                disabled={!selectedPdfForPreview || isGeneratingAdvanced}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg shadow-sm text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
              >
                {isGeneratingAdvanced ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin mr-2" />
                    ...
                  </>
                ) : (
                  <>
                    <BookOpen className="w-4 h-4 mr-2" />
                    🧠 Avanzato
                  </>
                )}
              </button>

              <button
                onClick={handleOCRExtract}
                disabled={!selectedPdfForPreview || isExtractingOCR}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg shadow-sm text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                title="Estrai testo con OCR (PDF scansionati/immagini)"
              >
                {isExtractingOCR ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin mr-2" />
                    ...
                  </>
                ) : (
                  <>
                    <Eye className="w-4 h-4 mr-2" />
                    🔍 OCR
                  </>
                )}
              </button>
            </div>
          </div>

          {/* 🎯 DROPDOWN Metodo OCR */}
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <label className="block text-sm font-medium text-blue-900 mb-2">
              ⚙️ Metodo OCR:
            </label>
            <select
              value={ocrMethod}
              onChange={(e) => setOcrMethod(e.target.value as 'tesseract' | 'llava' | 'claude-vision')}
              className="w-full px-4 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="tesseract">📝 Tesseract OCR</option>
              <option value="llava">🏠 Visual LLM - Locale (Gratis)</option>
              <option value="claude-vision">🤖 Visual LLM - Cloud</option>
            </select>
            <p className="text-xs text-blue-700 mt-2">
              {ocrMethod === 'tesseract' 
                ? '📝 OCR tradizionale - Veloce, ideale per documenti stampati con testo chiaro.'
                : ocrMethod === 'llava'
                ? '🏠 Visual LLM locale (LLaVA) - Gratis ma LENTO (~30-60s/pagina su CPU). Limite: 10 pagine.'
                : '🤖 Visual LLM cloud - AI avanzata, veloce, riconosce anche scrittura a mano. Limite: 10 pagine.'
              }
            </p>
          </div>

          {/* Statistiche */}
          {previewStats && (
            <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg text-sm">
              {previewStats.stats ? (
                // Statistiche Anteprima Normale
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <strong className="text-gray-700">📊 Statistiche Chunking:</strong><br />
                    • Totale chunks: {previewStats.total_chunks}<br />
                    • Chunks mostrati: {previewStats.displayed_chunks}<br />
                    • Metodo: {previewStats.stats.chunking_method}
                  </div>
                  <div>
                    <strong className="text-gray-700">📈 Dimensioni:</strong><br />
                    • Caratteri totali: {previewStats.stats.total_characters?.toLocaleString()}<br />
                    • Dimensione media chunk: {previewStats.stats.avg_chunk_size} caratteri<br />
                    • Modello embedding: {previewStats.stats.embed_model}
                  </div>
                </div>
              ) : previewStats.preprocessing_stats ? (
                // Statistiche Analisi Avanzata
                <div>
                  <div className="text-green-700 font-semibold mb-2">🧠 Preprocessing Avanzato Completato</div>
                  <div className="grid grid-cols-2 gap-4 text-xs">
                    <div>
                      <div className="font-medium text-gray-700">📝 Miglioramenti Testo:</div>
                      <div>• Parole de-sillabate: {previewStats.preprocessing_stats.dehyphenated_words}</div>
                      <div>• Righe ricomposte: {previewStats.preprocessing_stats.unwrapped_lines}</div>
                      <div>• Header/footer rimossi: {previewStats.preprocessing_stats.removed_headers}</div>
                      <div>• Legature corrette: {previewStats.preprocessing_stats.fixed_ligatures}</div>
                      <div>• Unicode normalizzato: {previewStats.preprocessing_stats.unicode_normalized ? 'Sì' : 'No'}</div>
                    </div>
                    <div>
                      <div className="font-medium text-gray-700">👥 Entità Estratte:</div>
                      <div>• Persone: {previewStats.entities_summary.persons}</div>
                      <div>• Organizzazioni: {previewStats.entities_summary.organizations}</div>
                      <div>• Leggi/Articoli: {previewStats.entities_summary.laws}</div>
                      <div>• Date: {previewStats.entities_summary.dates}</div>
                    </div>
                  </div>
                  <div className="mt-2 text-xs text-gray-600">
                    Totale chunks: {previewStats.total_chunks} (sentence-aware, overlap 80)
                  </div>
                </div>
              ) : null}
            </div>
          )}

          {/* Textarea Risultati */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              📝 Anteprima Chunks Generati:
            </label>
            <textarea
              value={previewResult}
              readOnly
              rows={20}
              className="w-full border-gray-300 rounded-md shadow-sm p-4 text-sm bg-white font-mono resize-y min-h-[400px] max-h-[500px]"
              placeholder="L'anteprima dei chunk comparirà qui dopo aver cliccato 'Genera Anteprima' o 'Analisi Avanzata'..."
            />
          </div>
          </div>
        </div>

        {/* 🔗 SEZIONE 3: GENERA INDICE */}
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div className="flex items-center mb-4">
            <Zap className="w-5 h-5 text-green-500 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">🔗 Genera Indice dai Selezionati</h3>
          </div>
          
          <p className="text-sm text-gray-600 mb-4">
            Crea un indice <strong>solo dai PDF selezionati</strong> nella sezione sopra.
          </p>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nome Indice:
              </label>
              <input
                type="text"
                value={indexName}
                onChange={(e) => setIndexName(e.target.value)}
                placeholder="es: manuale-hotel-2025"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>

            {/* ⚙️ Impostazioni Avanzate PDF (Chunking, Embedding, OCR) */}
            <div className="border-t border-gray-200 pt-4 mt-4">
              <button
                type="button"
                onClick={() => setShowPdfConfig(!showPdfConfig)}
                className="flex items-center justify-between w-full text-left text-gray-700 hover:text-gray-900 font-medium"
              >
                <span className="flex items-center">
                  <Settings className="w-5 h-5 mr-2 text-green-600" />
                  ⚙️ Impostazioni Avanzate (chunking, embedding, OCR)
                </span>
                {showPdfConfig ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
              {showPdfConfig && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">📄 Estrattore:</label>
                      <select
                        value={extractorType}
                        onChange={(e) => setExtractorType(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      >
                        <option value="pymupdf">PyMuPDF (Consigliato)</option>
                        <option value="pdfplumber">pdfplumber</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">🧠 Modello Embedding:</label>
                      <select
                        value={embedModel}
                        onChange={(e) => setEmbedModel(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      >
                        <option value="BAAI/bge-m3">BAAI/bge-m3 (Consigliato)</option>
                        <option value="sentence-transformers/all-MiniLM-L6-v2">all-MiniLM-L6-v2</option>
                        <option value="sentence-transformers/paraphrase-multilingual-MiniLM-L12-v2">paraphrase-multilingual-MiniLM-L12-v2</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">📏 Dimensione Chunk:</label>
                      <input
                        type="number"
                        value={chunkSize}
                        onChange={(e) => setChunkSize(Number(e.target.value))}
                        min={100}
                        max={2000}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">🔄 Overlap:</label>
                      <input
                        type="number"
                        value={chunkOverlap}
                        onChange={(e) => setChunkOverlap(Number(e.target.value))}
                        min={0}
                        max={500}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    </div>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={useSemanticChunking}
                        onChange={(e) => setUseSemanticChunking(e.target.checked)}
                        className="h-4 w-4 text-blue-600 rounded"
                      />
                      <span className="ml-2 text-sm font-medium text-blue-800">🧠 Chunking Semantico (migliore qualità, più lento)</span>
                    </label>
                    <p className="text-xs text-gray-500 mt-1 ml-6">Per PDF lunghi (50+ pagine) tieni disattivato per indicizzare in secondi invece che minuti.</p>
                    {useSemanticChunking && (
                      <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Soglia Similarità:</label>
                          <input
                            type="number"
                            value={similarityThreshold}
                            onChange={(e) => setSimilarityThreshold(Number(e.target.value))}
                            min={0.1}
                            max={1}
                            step={0.05}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Min Caratteri:</label>
                          <input
                            type="number"
                            value={minChunkChars}
                            onChange={(e) => setMinChunkChars(Number(e.target.value))}
                            min={50}
                            max={1000}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Max Caratteri:</label>
                          <input
                            type="number"
                            value={maxChunkChars}
                            onChange={(e) => setMaxChunkChars(Number(e.target.value))}
                            min={500}
                            max={5000}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-4">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={enableOCR}
                        onChange={(e) => setEnableOCR(e.target.checked)}
                        className="h-4 w-4 text-green-600 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">🔍 OCR per PDF scansionati</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={usePresidio}
                        onChange={(e) => setUsePresidio(e.target.checked)}
                        className="h-4 w-4 text-blue-600 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">🔒 Anonimizzazione Presidio</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={enableMultiModal}
                        onChange={(e) => setEnableMultiModal(e.target.checked)}
                        className="h-4 w-4 text-purple-600 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">🖼️ Multi-Modal (Beta)</span>
                    </label>
                  </div>
                  <div className="text-xs text-gray-500 pt-2 border-t border-gray-200">
                    📊 Attuale: {useSemanticChunking ? 'Semantico' : 'Fisso'} · Chunk {chunkSize} · Overlap {chunkOverlap}
                    {useSemanticChunking && ` · Soglia ${similarityThreshold} · ${minChunkChars}-${maxChunkChars} char`}
                  </div>
                </div>
              )}
            </div>

            {/* Esegui in background: continua anche chiudendo la pagina */}
            <label className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg cursor-pointer">
              <input
                type="checkbox"
                checked={runPdfInBackground}
                onChange={(e) => setRunPdfInBackground(e.target.checked)}
                className="h-4 w-4 text-amber-600 rounded"
              />
              <span className="text-sm font-medium text-amber-900">
                📦 Esegui in background (puoi chiudere la pagina; vedi &quot;Processi in background&quot;)
              </span>
            </label>

            <button
              onClick={handleGenerateIndex}
              disabled={selectedPdfFiles.size === 0 || !indexName.trim()}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              <Zap className="w-5 h-5 mr-2" />
              ⚡ Genera Indice da {selectedPdfFiles.size} PDF
            </button>
            
            {selectedPdfFiles.size === 0 && (
              <p className="text-sm text-orange-600 text-center">
                ⚠️ Seleziona almeno un PDF nella sezione sopra
              </p>
            )}
          </div>
        </div>

        {/* Sezione Impostazioni (Collassabile) */}
        <div className="mt-6 bg-white border-2 border-gray-300 rounded-lg overflow-hidden">
          <button
            onClick={() => setShowConfig(!showConfig)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center">
              <Settings className="w-5 h-5 text-gray-700 mr-2" />
              <h3 className="font-bold text-gray-900">⚙️ Impostazioni Avanzate</h3>
            </div>
            {showConfig ? <ChevronUp className="w-5 h-5 text-gray-600" /> : <ChevronDown className="w-5 h-5 text-gray-600" />}
          </button>
          
          {showConfig && (
            <div className="p-6 border-t border-gray-200 space-y-6 bg-gray-50">
              
              {/* Opzioni di Embedding e Chunking */}
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <h4 className="text-md font-bold text-gray-800 mb-4">🧠 Opzioni di Embedding e Chunking</h4>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Colonna Sinistra */}
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        📄 Estrattore Testo:
                      </label>
                      <select
                        value={extractorType}
                        onChange={(e) => setExtractorType(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      >
                        <option value="pymupdf">PyMuPDF (Consigliato)</option>
                        <option value="pdfplumber">pdfplumber</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        🧠 Modello Embedding:
                      </label>
                      <select
                        value={embedModel}
                        onChange={(e) => setEmbedModel(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      >
                        <option value="BAAI/bge-m3">BAAI/bge-m3 (Consigliato)</option>
                        <option value="sentence-transformers/all-MiniLM-L6-v2">all-MiniLM-L6-v2</option>
                        <option value="sentence-transformers/paraphrase-multilingual-MiniLM-L12-v2">paraphrase-multilingual-MiniLM-L12-v2</option>
                      </select>
                    </div>
                  </div>
                  
                  {/* Colonna Destra */}
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          📏 Dimensione Chunk:
                        </label>
                        <input
                          type="number"
                          value={chunkSize}
                          onChange={(e) => setChunkSize(Number(e.target.value))}
                          min="100"
                          max="2000"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          🔄 Overlap:
                        </label>
                        <input
                          type="number"
                          value={chunkOverlap}
                          onChange={(e) => setChunkOverlap(Number(e.target.value))}
                          min="0"
                          max="500"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        />
                      </div>
                    </div>
                    
                    {/* Chunking Semantico */}
                    <div className="border border-blue-200 bg-blue-50 p-3 rounded-lg">
                      <div className="flex items-center mb-2">
                        <input
                          id="semantic-chunking"
                          type="checkbox"
                          checked={useSemanticChunking}
                          onChange={(e) => setUseSemanticChunking(e.target.checked)}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <label htmlFor="semantic-chunking" className="ml-2 block text-sm font-medium text-blue-700">
                          🧠 Usa Chunking Semantico
                        </label>
                      </div>
                      
                      {useSemanticChunking && (
                        <div className="mt-3 space-y-2">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Soglia Similarità:
                            </label>
                            <input
                              type="number"
                              value={similarityThreshold}
                              onChange={(e) => setSimilarityThreshold(Number(e.target.value))}
                              min="0.1"
                              max="1.0"
                              step="0.05"
                              className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                Min Caratteri:
                              </label>
                              <input
                                type="number"
                                value={minChunkChars}
                                onChange={(e) => setMinChunkChars(Number(e.target.value))}
                                min="50"
                                max="1000"
                                className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                Max Caratteri:
                              </label>
                              <input
                                type="number"
                                value={maxChunkChars}
                                onChange={(e) => setMaxChunkChars(Number(e.target.value))}
                                min="500"
                                max="5000"
                                className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* OCR e Estrazione Avanzata */}
              <div className="bg-white rounded-lg p-4 border border-green-200">
                <h4 className="text-md font-bold text-green-700 mb-3">🤖 OCR e Estrazione Avanzata</h4>
                <p className="text-xs text-gray-500 mb-3">
                  Opzioni per estrarre testo da PDF scansionati o di bassa qualità.
                </p>
                
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-center">
                    <input
                      id="enable-ocr"
                      type="checkbox"
                      checked={enableOCR}
                      onChange={(e) => setEnableOCR(e.target.checked)}
                      className="h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                    />
                    <label htmlFor="enable-ocr" className="ml-2 block text-sm font-medium text-green-700">
                      🔍 Abilita OCR per PDF scansionati
                    </label>
                  </div>
                  <p className="text-xs text-green-600 ml-6 mt-1">
                    Utilizza OCR quando l'estrazione normale fallisce. Più lento ma più accurato.
                  </p>
                </div>
              </div>

              {/* Anonimizzazione */}
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <h4 className="text-md font-bold text-gray-800 mb-3">🔒 Anonimizzazione Professionale</h4>
                <p className="text-xs text-gray-400 mb-3">
                  Usa Microsoft Presidio per identificare Dati Personali e/o sensibili.{' '}
                  <a 
                    href="https://github.com/microsoft/presidio/blob/main/LICENSE" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-blue-500 hover:text-blue-600 underline"
                  >
                    Licenza MIT
                  </a>
                </p>
                
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <div className="flex items-center">
                    <input
                      id="use-presidio"
                      type="checkbox"
                      checked={usePresidio}
                      onChange={(e) => setUsePresidio(e.target.checked)}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="use-presidio" className="ml-2 block text-sm font-medium text-blue-700">
                      🧠 Usa Modelli professionali Italiani
                    </label>
                  </div>
                  <p className="text-xs text-blue-600 ml-6 mt-1">
                    Anonimizzazione avanzata di Microsoft Presidio per confronto.
                  </p>
                </div>
              </div>
              
              {/* 🆕 Multi-Modal RAG */}
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-purple-700 mb-1">🖼️ Multi-Modal RAG (Beta)</h4>
                <p className="text-xs text-gray-600 leading-relaxed mb-2">
                  Estrazione avanzata di <strong>immagini</strong> e <strong>tabelle</strong> dai PDF con BLIP-2 e CLIP embeddings per retrieval visuale.
                </p>
                
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                  <div className="flex items-center">
                    <input
                      id="enable-multimodal"
                      type="checkbox"
                      checked={enableMultiModal}
                      onChange={(e) => setEnableMultiModal(e.target.checked)}
                      className="h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                    />
                    <label htmlFor="enable-multimodal" className="ml-2 block text-sm font-medium text-purple-700">
                      🚀 Attiva Multi-Modal
                    </label>
                  </div>
                  <p className="text-xs text-purple-600 ml-6 mt-1">
                    Estrae e indicizza immagini, grafici e tabelle dai PDF. Richiede GPU per performance ottimali.
                  </p>
                </div>
                
                {enableMultiModal && (
                  <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3 text-xs text-yellow-800">
                    ⚠️ <strong>Beta:</strong> Multi-Modal richiede dipendenze opzionali (transformers, BLIP-2, CLIP). 
                    Esegui: <code className="bg-yellow-100 px-1 py-0.5 rounded">./install_multimodal.sh</code>
                  </div>
                )}
              </div>

              {/* Riepilogo Configurazione */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-bold text-blue-900 mb-2">📊 Riepilogo Configurazione Attuale:</h4>
                <div className="grid grid-cols-2 gap-2 text-xs text-blue-800">
                  <div><span className="font-semibold">Estrattore:</span> {extractorType}</div>
                  <div><span className="font-semibold">Embedding:</span> {embedModel.split('/').pop()}</div>
                  <div><span className="font-semibold">Chunk Size:</span> {chunkSize}</div>
                  <div><span className="font-semibold">Overlap:</span> {chunkOverlap}</div>
                  <div><span className="font-semibold">Chunking Semantico:</span> {useSemanticChunking ? '✅ Sì' : '❌ No'}</div>
                  <div><span className="font-semibold">OCR:</span> {enableOCR ? '✅ Attivo' : '❌ Disattivo'}</div>
                </div>
              </div>
            </div>
          )}
        </div>


          </div>
        </div>

        {/* 🌐 SEZIONE WEB CRAWLER - Indicizzazione Siti Web (espandibile) */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6 border border-gray-200">
          <button
            type="button"
            onClick={() => setShowWebCrawlerSection(!showWebCrawlerSection)}
            className="flex items-center justify-between w-full text-left mb-4"
          >
            <div className="flex items-center">
              <Globe className="w-6 h-6 text-green-500 mr-2" />
              <h2 className="text-xl font-bold text-gray-900">🌐 Web Crawler</h2>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-red-600 font-semibold text-sm">⚠️ Richiede Piano Custom</span>
              {showWebCrawlerSection ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </div>
          </button>

          <div className={showWebCrawlerSection ? "" : "hidden"}>
          <p className="text-sm text-gray-600 mb-4">
            Esegui il crawling di un sito web per indicizzarne i contenuti.
            {typeof window !== 'undefined' && window.location?.hostname === 'studio.oystar.ai' && (
              <span className="block mt-1 text-xs text-blue-600">Da qui le chiamate vanno al backend flow.oystar.ai. Il crawler richiede Python 3.11 e Scrapy sul server.</span>
            )}
          </p>

          <div className="space-y-4">
            {/* 🌐 URL DI PARTENZA - PRIMA COSA */}
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border-2 border-blue-300 rounded-lg p-4">
              <label className="block text-sm font-bold text-gray-800 mb-2">
                🌐 URL da Crawlare (uno per riga):
              </label>
              <textarea
                value={crawlUrl}
                onChange={(e) => {
                  const val = e.target.value;
                  setCrawlUrl(val);
                  const urls = val.split('\n').map(url => url.trim()).filter(url => url);
                  if (urls.length > 0) {
                    try {
                      const firstUrl = new URL(urls[0]);
                      const hostname = firstUrl.hostname;
                      setDomainFilter(hostname);
                      // Nome indice default: prime 3 lettere alfabetiche del dominio + 001 (es. marinuzzi.it → mar001)
                      const withoutWww = hostname.replace(/^www\./i, '');
                      const base = withoutWww.split('.')[0] || withoutWww;
                      const letters = base.replace(/[^a-zA-Z]/g, '').toLowerCase().slice(0, 3);
                      const prefix = letters || 'web';
                      setWebIndexName((prev) => (prev === '' || /^[a-z]{3}\d{3}$/.test(prev) ? `${prefix}001` : prev));
                    } catch (_) {
                      // URL non valido, ignora
                    }
                  }
                }}
                rows={3}
                placeholder="https://www.marinuzzi.it&#10;https://www.marinuzzi.it/prodotti"
                className="w-full px-4 py-2 border-2 border-blue-400 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y font-mono text-sm"
              />
              <div className="mt-2 p-2 bg-white rounded border border-blue-200">
                <p className="text-xs font-semibold text-blue-800 mb-1">
                  💡 <strong>Il dominio viene estratto automaticamente</strong> e inserito nel campo "Dominio da Seguire" qui sotto
                </p>
              </div>
            </div>

            {/* 🔒 DOMINIO DA SEGUIRE - SEMPLIFICATO */}
            <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4">
              <label className="block text-sm font-bold text-gray-800 mb-2">
                🔒 Dominio da Seguire:
              </label>
              <input
                type="text"
                value={domainFilter}
                onChange={(e) => setDomainFilter(e.target.value)}
                placeholder="es. www.marinuzzi.it"
                className="w-full px-4 py-2 border-2 border-blue-400 rounded-lg bg-white font-mono text-sm font-bold text-gray-800"
              />
              <p className="text-xs text-blue-700 mt-2">
                ✅ Il crawler seguirà SOLO URL che contengono questo dominio nell'indirizzo completo.
              </p>
              
              {/* Checkbox Permetti Siti Terzi */}
              <label className="flex items-center space-x-2 cursor-pointer mt-3 p-2 bg-white rounded border border-blue-200">
                <input
                  type="checkbox"
                  checked={allowExternalSites}
                  onChange={(e) => setAllowExternalSites(e.target.checked)}
                  className="form-checkbox h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  🌐 Permetti Siti Terzi (se attivo, può uscire dal dominio specificato)
                </span>
              </label>
              {!allowExternalSites && (
                <p className="text-xs text-red-600 mt-1 font-semibold">
                  🚫 I link a domini esterni (Google, Facebook, oystar.ai, etc.) verranno <strong>automaticamente ignorati</strong>.
                </p>
              )}
            </div>

            {/* Nome Indice e Suffisso */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  📝 Nome Indice:
                </label>
                <input
                  type="text"
                  value={webIndexName}
                  onChange={(e) => setWebIndexName(e.target.value)}
                  placeholder="es. mar001 (auto: prime 3 lettere dominio + 001)"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  📝 Suffisso Indice (opzionale):
                </label>
                <input
                  type="text"
                  value={crawlIndexSuffix}
                  onChange={(e) => setCrawlIndexSuffix(e.target.value)}
                  placeholder="es: azienda_web"
                  maxLength={20}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
                <p className="text-xs text-gray-500 mt-1">Max 20 caratteri</p>
              </div>
            </div>

            {/* Parametri Base */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Profondità */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  🔍 Profondità Crawl:
                </label>
                <input
                  type="number"
                  value={crawlDepth}
                  onChange={(e) => setCrawlDepth(parseInt(e.target.value) || 1)}
                  min="1"
                  max="5"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
                <p className="text-xs text-gray-500 mt-1">Quanto in profondità seguire i link (1-5). Per pochi livelli usa 1 o 2.</p>
              </div>

              {/* Keywords Focus */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  🎯 Parole Chiave Focus:
                </label>
                <input
                  type="text"
                  value={focusKeywords}
                  onChange={(e) => setFocusKeywords(e.target.value)}
                  placeholder="es. bilancio, sostenibilità"
                  maxLength={40}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
                <p className="text-xs text-gray-500 mt-1">Filtra pagine con queste parole (opzionale, max 40 char)</p>
              </div>
            </div>

            </div>

            {/* ⚙️ SEZIONE IMPOSTAZIONI AVANZATE CRAWLER */}
            <div className="border-t pt-4 mt-4">
              <button
                onClick={() => setShowConfig(!showConfig)}
                className="flex items-center text-indigo-600 hover:text-indigo-700 font-medium mb-3"
              >
                {showConfig ? <ChevronUp className="w-5 h-5 mr-2" /> : <ChevronDown className="w-5 h-5 mr-2" />}
                <Settings className="w-5 h-5 mr-2" />
                ⚙️ Impostazioni Avanzate Crawler
              </button>

              {showConfig && (
                <div className="space-y-5 bg-gray-50 p-4 rounded-lg">
                  {/* ═══════════════════════════════════════════════════════ */}
                  {/* PARTE 1: MODALITÀ NAVIGAZIONE – Come esploriamo il sito */}
                  {/* ═══════════════════════════════════════════════════════ */}
                  <div className="border-l-4 border-teal-500 pl-3">
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <div>
                        <h4 className="text-sm font-semibold text-teal-800">🌐 Modalità di navigazione</h4>
                        <p className="text-xs text-gray-500">Scegli come esplorare il sito (auto-suggerimento disponibile)</p>
                      </div>
                      <button
                        onClick={handleSuggestNavigation}
                        disabled={isDetectingSuggestion || !crawlUrl.trim()}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-100 hover:bg-amber-200 border border-amber-300 text-amber-800 font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        title="Analizza il sito e suggerisci la modalità ottimale"
                      >
                        {isDetectingSuggestion ? <Loader className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                        {isDetectingSuggestion ? 'Analisi...' : 'Suggerimento'}
                      </button>
                    </div>
                    {suggestionMessage && (
                      <p className="text-xs text-teal-700 bg-teal-50 border border-teal-200 rounded px-2 py-1.5 mb-3">{suggestionMessage}</p>
                    )}
                    <div className="space-y-3">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <label className="flex items-start gap-2 cursor-pointer bg-purple-50 px-3 py-2.5 rounded-lg border border-purple-200">
                          <input type="checkbox" checked={useFastMode} onChange={(e) => setUseFastMode(e.target.checked)} className="form-checkbox h-4 w-4 mt-0.5 text-purple-600 rounded flex-shrink-0" />
                          <div>
                            <span className="text-sm font-semibold text-purple-800 block">🚀 Fast Mode (Scrapy)</span>
                            <span className="text-xs text-purple-600">10x più veloce, siti statici HTML</span>
                          </div>
                        </label>
                        <label className={`flex items-start gap-2 cursor-pointer px-3 py-2.5 rounded-lg border transition-colors ${useFastMode ? 'bg-gray-50 border-gray-200 opacity-70 cursor-not-allowed' : 'bg-indigo-50 border-indigo-200'}`}>
                          <input type="checkbox" checked={usePlaywright} onChange={(e) => setUsePlaywright(e.target.checked)} className="form-checkbox h-4 w-4 mt-0.5 text-indigo-600 rounded flex-shrink-0" disabled={useFastMode} />
                          <div>
                            <span className={`text-sm font-medium block ${useFastMode ? 'text-gray-400' : 'text-indigo-800'}`}>🎭 Playwright</span>
                            <span className={`text-xs ${useFastMode ? 'text-gray-400' : 'text-indigo-600'}`}>SPA, contenuto dinamico, JS</span>
                          </div>
                        </label>
                        <label className={`flex items-start gap-2 cursor-pointer px-3 py-2.5 rounded-lg border transition-colors ${useFastMode ? 'bg-gray-50 border-gray-200 opacity-70 cursor-not-allowed' : 'bg-blue-50 border-blue-200'}`}>
                          <input type="checkbox" checked={enableJavaScript} onChange={(e) => setEnableJavaScript(e.target.checked)} className="form-checkbox h-4 w-4 mt-0.5 text-blue-600 rounded flex-shrink-0" disabled={useFastMode} />
                          <div>
                            <span className={`text-sm font-medium block ${useFastMode ? 'text-gray-400' : 'text-blue-800'}`}>⚡ Attiva JS</span>
                            <span className={`text-xs ${useFastMode ? 'text-gray-400' : 'text-blue-600'}`}>Esegui JavaScript nelle pagine</span>
                          </div>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* ═══════════════════════════════════════════════════════ */}
                  {/* PARTE 1b: CONTENUTI DA SCARICARE – PDF, OCR, ecc. */}
                  {/* ═══════════════════════════════════════════════════════ */}
                  {!useFastMode && (
                    <div className="border-l-4 border-orange-400 pl-3">
                      <h4 className="text-sm font-semibold text-orange-800 mb-2">📥 Contenuti da scaricare</h4>
                      <p className="text-xs text-gray-500 mb-3">Scegli quali tipi di file includere e se applicare OCR.</p>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                        <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={downloadPdfs} onChange={(e) => setDownloadPdfs(e.target.checked)} className="form-checkbox h-3.5 w-3.5 text-indigo-600" /><span>📄 Scarica PDF</span></label>
                        <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={downloadOnlyPdfs} onChange={(e) => setDownloadOnlyPdfs(e.target.checked)} className="form-checkbox h-3.5 w-3.5 text-orange-600" /><span>🚀 Solo PDF</span></label>
                        <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={downloadImages} onChange={(e) => setDownloadImages(e.target.checked)} className="form-checkbox h-3.5 w-3.5 text-green-600" /><span>📸 Immagini</span></label>
                        <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={enableOcrImages} onChange={(e) => setEnableOcrImages(e.target.checked)} className="form-checkbox h-3.5 w-3.5 text-green-600" /><span>🤖 OCR immagini</span></label>
                        <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={enableOcrPdfs} onChange={(e) => setEnableOcrPdfs(e.target.checked)} className="form-checkbox h-3.5 w-3.5 text-green-600" /><span>👁️ OCR PDF</span></label>
                      </div>
                    </div>
                  )}

                  {/* ═══════════════════════════════════════════════════════ */}
                  {/* PARTE 2: CHUNKING & EMBEDDING – Come processiamo il contenuto estratto */}
                  {/* ═══════════════════════════════════════════════════════ */}
                  <div className="border-l-4 border-indigo-500 pl-3">
                    <h4 className="text-sm font-semibold text-indigo-800 mb-2">📦 Chunking & Embedding</h4>
                    <p className="text-xs text-gray-500 mb-3">Parametri per spezzare il testo e creare gli embedding dell’indice.</p>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Modello embedding</label>
                        <select value={embedModel} onChange={(e) => setEmbedModel(e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500">
                          <option value="BAAI/bge-m3">BAAI/bge-m3 (consigliato)</option>
                          <option value="sentence-transformers/all-MiniLM-L6-v2">all-MiniLM-L6-v2</option>
                          <option value="intfloat/e5-small-v2">e5-small-v2</option>
                          <option value="Linq-AI-Research/Linq-Embed-Mistral">Linq-Embed-Mistral</option>
                          <option value="intfloat/multilingual-e5-large-instruct">multilingual-e5-large</option>
                        </select>
                      </div>
                      <div className="flex flex-wrap gap-4 items-center">
                        <div className="flex items-center gap-2">
                          <label className="text-xs font-medium text-gray-600">Batch</label>
                          <input type="number" value={crawlerBatchSize} onChange={(e) => setCrawlerBatchSize(parseInt(e.target.value) || 1)} min="1" max="100" className="w-14 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500" />
                        </div>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={useSemanticChunking} onChange={(e) => setUseSemanticChunking(e.target.checked)} className="form-checkbox h-3.5 w-3.5 text-purple-600 rounded" />
                          <span className="text-xs font-medium text-gray-700">Chunking semantico</span>
                        </label>
                      </div>
                      {useSemanticChunking ? (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div><label className="block text-xs font-medium text-gray-600 mb-1">Soglia similarità</label><input type="number" value={similarityThreshold} onChange={(e) => setSimilarityThreshold(parseFloat(e.target.value))} min="0" max="1" step="0.05" className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-purple-500" /></div>
                          <div><label className="block text-xs font-medium text-gray-600 mb-1">Min char</label><input type="number" value={minChunkChars} onChange={(e) => setMinChunkChars(parseInt(e.target.value))} min="50" max="500" className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-purple-500" /></div>
                          <div><label className="block text-xs font-medium text-gray-600 mb-1">Max char</label><input type="number" value={maxChunkChars} onChange={(e) => setMaxChunkChars(parseInt(e.target.value))} min="500" max="3000" className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-purple-500" /></div>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div><label className="block text-xs font-medium text-gray-600 mb-1">Dimensione chunk</label><input type="number" value={chunkSize} onChange={(e) => setChunkSize(parseInt(e.target.value))} min="100" max="1000" className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500" /></div>
                          <div><label className="block text-xs font-medium text-gray-600 mb-1">Overlap</label><input type="number" value={chunkOverlap} onChange={(e) => setChunkOverlap(parseInt(e.target.value))} min="0" max="200" className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500" /></div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Bottoni Crawling */}
            <div className="flex gap-2">
              <button
                onClick={handleCrawlWithStreaming}
                disabled={isCrawling || !crawlUrl.trim() || !webIndexName.trim()}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isCrawling ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin mr-2" />
                    ⚡ Crawling...
                  </>
                ) : (
                  <>
                    <Search className="w-5 h-5 mr-2" />
                    ⚡ Avvia
                  </>
                )}
              </button>

              <button
                onClick={handleCrawlBackground}
                disabled={isCrawling || !crawlUrl.trim() || !webIndexName.trim()}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                <span className="mr-2">🚀</span>
                Avvia in Background
              </button>
              
              <button
                onClick={clearCrawlerFields}
                disabled={isCrawling}
                className="px-4 py-3 bg-gray-500 hover:bg-gray-600 text-white font-bold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                title="Pulisci tutti i campi del crawler"
              >
                <span className="mr-2">🧹</span>
                Pulisci
              </button>

              <button
                onClick={() => { setShowLogPanel(!showLogPanel); if (!showLogPanel) setShowDebugPanel(true); }}
                className={`px-4 py-3 font-bold rounded-lg transition-colors flex items-center justify-center border-2 ${showLogPanel ? 'bg-gray-800 text-green-400 border-green-500' : 'bg-gray-800 hover:bg-gray-700 text-green-300 border-gray-600'}`}
                title="Apri/chiudi il log di trasparenza dell'algoritmo (URL trovate, filtrate, coda, chunks)"
              >
                <span className="mr-2">📜</span>
                Log
              </button>
            </div>

            {/* 📜 Pannello Log – trasparenza algoritmo (visibile quando showLogPanel) */}
            {showLogPanel && (
              <div className="mt-4 rounded-lg overflow-hidden border-2 border-green-600 shadow-xl">
                <div className="bg-gray-900 text-green-400 p-3 font-mono text-xs flex flex-wrap items-center justify-between gap-2 border-b border-green-600">
                  <span className="font-bold text-sm">📜 Log – Trasparenza algoritmo</span>
                  <div className="flex items-center gap-2">
                    {activeCrawls.length > 1 && (
                      <select
                        value={selectedLogCrawlId ?? (isCrawling ? '__current__' : (activeCrawls[0]?.crawl_id ?? ''))}
                        onChange={(e) => setSelectedLogCrawlId(e.target.value === '__current__' ? null : e.target.value || null)}
                        className="bg-black text-green-400 border border-green-600 rounded px-2 py-1 text-xs"
                      >
                        {isCrawling && <option value="__current__">Crawl corrente (streaming)</option>}
                        {activeCrawls.map((c: any) => (
                          <option key={c.crawl_id} value={c.crawl_id}>
                            {c.crawl_id} {c.config?.seeds?.[0] ? ` – ${String(c.config.seeds[0]).slice(0, 40)}…` : ''}
                          </option>
                        ))}
                      </select>
                    )}
                    <button
                      onClick={() => setShowLogPanel(false)}
                      className="text-red-400 hover:text-red-300 px-2"
                    >
                      ✕ Chiudi
                    </button>
                  </div>
                </div>
                <div className="bg-black text-green-400 p-4 font-mono text-xs min-h-[320px] max-h-[480px] flex flex-col">
                  {activeCrawls.length > 1 && selectedLogCrawlId && selectedLogCrawlId !== '__current__' && !isCrawling && (
                    <div className="mb-3 p-2 bg-amber-900/50 text-amber-300 rounded border border-amber-600">
                      Log in tempo reale non disponibile per crawl in background (ID: {selectedLogCrawlId}). Usa &quot;Avvia&quot; per vedere il flusso live. Dettagli processo sopra.
                    </div>
                  )}
                  {(!isCrawling && crawlLog.length === 0 && crawlDebug.urls_in_queue.length === 0) && !(activeCrawls.length > 1 && selectedLogCrawlId && selectedLogCrawlId !== '__current__') && (
                    <div className="text-gray-500 mb-3">
                      {activeCrawls.length === 1 ? (
                        <>Un crawl è in esecuzione in background. Il log in tempo reale (URL trovate, coda, chunks) è disponibile solo avviando con &quot;Avvia&quot;.</>
                      ) : (
                        <>
                          Nessun log. Clicca &quot;Avvia&quot; o &quot;Avvia in Background&quot; per vedere in tempo reale: URL trovate, URL filtrate (buone), coda da visitare (L e Δ), chunks.
                          <br />
                          <span className="text-gray-600">Per verificare il crawler: inserisci <kbd className="px-1 bg-gray-800 rounded">https://example.com</kbd> e clicca Avvia.</span>
                        </>
                      )}
                    </div>
                  )}
                  {/* Metriche in tempo reale */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3 text-green-300">
                    <div><span className="text-gray-500">📥 URL trovate:</span> {crawlDebug.urls_visited.length}</div>
                    <div><span className="text-gray-500">✅ In coda (accettate):</span> {crawlDebug.urls_in_queue.length}</div>
                    <div><span className="text-gray-500">📋 Coda L:</span> {crawlDebug.urls_in_queue.length} {crawlDebug.queue_delta != null && <span className="text-cyan-400">(Δ {crawlDebug.queue_delta >= 0 ? '+' : ''}{crawlDebug.queue_delta})</span>}</div>
                    <div><span className="text-gray-500">📦 Chunks:</span> {crawlDebug.total_chunks}</div>
                    <div><span className="text-gray-500">✅ Processate:</span> {crawlDebug.urls_processed.length}</div>
                  </div>
                  {/* Log testuale */}
                  <div className="flex-1 min-h-0 overflow-y-auto bg-black/50 rounded p-2 border border-gray-700">
                    {crawlLog.map((log, idx) => (
                      <div key={idx} className="mb-0.5 text-green-400">{log}</div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* 📋 Log e Progress del Crawling (compatibilità: mostra anche se Log chiuso ma crawl attivo) */}
            {(isCrawling || crawlProgress.phase !== 'idle' || showDebugPanel) && !showLogPanel && (
              <div className="mt-4 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
                {/* Toggle Debug Panel */}
                {!showDebugPanel && (isCrawling || crawlProgress.phase !== 'idle') && (
                  <button
                    onClick={() => setShowDebugPanel(true)}
                    className="mb-3 px-3 py-1 bg-black text-green-400 rounded text-xs font-mono hover:bg-gray-800"
                  >
                    🐛 Mostra Debug Panel
                  </button>
                )}
                {/* Progress Info */}
                <div className="grid grid-cols-3 gap-2 mb-3 text-sm">
                  <div className="bg-white p-2 rounded shadow-sm">
                    <div className="text-gray-500 text-xs">Pagine</div>
                    <div className="font-bold text-blue-600">{crawlProgress.pages_processed}</div>
                  </div>
                  <div className="bg-white p-2 rounded shadow-sm">
                    <div className="text-gray-500 text-xs">Chunks</div>
                    <div className="font-bold text-green-600">{crawlProgress.chunks_created}</div>
                  </div>
                  <div className="bg-white p-2 rounded shadow-sm">
                    <div className="text-gray-500 text-xs">Stato</div>
                    <div className="font-bold text-purple-600">
                      {crawlProgress.phase === 'crawling' && '🌐 Crawling'}
                      {crawlProgress.phase === 'completed' && '✅ Completato'}
                      {crawlProgress.phase === 'idle' && '⏸️ Idle'}
                    </div>
                  </div>
                </div>

                {/* Current URL */}
                {crawlProgress.current_url && (
                  <div className="mb-3 p-2 bg-white rounded text-xs">
                    <div className="text-gray-500 mb-1">URL corrente:</div>
                    <div className="text-blue-600 truncate">{crawlProgress.current_url}</div>
                  </div>
                )}

                {/* Log Area */}
                <div className="bg-gray-900 text-green-400 p-3 rounded font-mono text-xs h-48 overflow-y-auto">
                  {crawlLog.map((log, idx) => (
                    <div key={idx} className="mb-1">{log}</div>
                  ))}
                </div>
                
                {/* 🐛 Pannello Debug Dettagliato */}
                {showDebugPanel && (
                  <div className="mt-4 bg-black text-green-400 p-4 rounded-lg border-2 border-green-500 font-mono text-xs">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-green-400 font-bold text-sm">🐛 DEBUG PANEL - Crawler Details</h3>
                      <button
                        onClick={() => setShowDebugPanel(false)}
                        className="text-red-400 hover:text-red-300"
                      >
                        ✕ Chiudi
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      {/* Statistiche */}
                      <div className="bg-gray-900 p-3 rounded">
                        <div className="text-yellow-400 mb-2">📊 Statistiche</div>
                        <div>⏱️ Tempo: {crawlDebug.start_time ? `${Math.floor((Date.now() - crawlDebug.start_time) / 1000)}s` : 'N/A'}</div>
                        <div>📄 In coda: {crawlDebug.urls_in_queue.length}</div>
                        <div>🌐 Visitati: {crawlDebug.urls_visited.length}</div>
                        <div>✅ Processati: {crawlDebug.urls_processed.length}</div>
                        <div>❌ Falliti: {crawlDebug.urls_failed.length}</div>
                        <div>📦 Chunks totali: {crawlDebug.total_chunks}</div>
                      </div>
                      
                      {/* Errori */}
                      {crawlDebug.errors.length > 0 && (
                        <div className="bg-red-900 p-3 rounded">
                          <div className="text-red-400 mb-2">❌ Errori ({crawlDebug.errors.length})</div>
                          <div className="max-h-32 overflow-y-auto">
                            {crawlDebug.errors.slice(-5).map((err, idx) => (
                              <div key={idx} className="text-red-300 text-xs mb-1">{err}</div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* URL in coda */}
                    {crawlDebug.urls_in_queue.length > 0 && (
                      <div className="mb-3">
                        <div className="text-yellow-400 mb-1">📋 URL in coda ({crawlDebug.urls_in_queue.length}):</div>
                        <div className="bg-gray-900 p-2 rounded max-h-24 overflow-y-auto">
                          {crawlDebug.urls_in_queue.slice(0, 10).map((url, idx) => (
                            <div key={idx} className="text-green-300 text-xs truncate">{idx + 1}. {url}</div>
                          ))}
                          {crawlDebug.urls_in_queue.length > 10 && (
                            <div className="text-gray-500 text-xs">... e altri {crawlDebug.urls_in_queue.length - 10}</div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* URL visitati */}
                    {crawlDebug.urls_visited.length > 0 && (
                      <div className="mb-3">
                        <div className="text-blue-400 mb-1">🌐 URL visitati ({crawlDebug.urls_visited.length}):</div>
                        <div className="bg-gray-900 p-2 rounded max-h-24 overflow-y-auto">
                          {crawlDebug.urls_visited.slice(-10).map((url, idx) => (
                            <div key={idx} className="text-blue-300 text-xs truncate">{url}</div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* URL processati con chunks */}
                    {crawlDebug.urls_processed.length > 0 && (
                      <div className="mb-3">
                        <div className="text-green-400 mb-1">✅ URL processati ({crawlDebug.urls_processed.length}):</div>
                        <div className="bg-gray-900 p-2 rounded max-h-32 overflow-y-auto">
                          {crawlDebug.urls_processed.slice(-10).map((url, idx) => (
                            <div key={idx} className="text-green-300 text-xs truncate">
                              {url} {crawlDebug.chunks_by_url[url] ? `→ ${crawlDebug.chunks_by_url[url]} chunks` : ''}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* URL falliti */}
                    {crawlDebug.urls_failed.length > 0 && (
                      <div className="mb-3">
                        <div className="text-red-400 mb-1">❌ URL falliti ({crawlDebug.urls_failed.length}):</div>
                        <div className="bg-gray-900 p-2 rounded max-h-24 overflow-y-auto">
                          {crawlDebug.urls_failed.slice(-5).map((item, idx) => (
                            <div key={idx} className="text-red-300 text-xs">
                              <div className="truncate">{item.url}</div>
                              <div className="text-red-400 text-xs ml-2">→ {item.error}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <div className="text-gray-500 text-xs mt-2">
                      Ultimo aggiornamento: {crawlDebug.last_update ? new Date(crawlDebug.last_update).toLocaleTimeString() : 'N/A'}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          </div>
        </div>

        {/* 📂 SEZIONE ULTIMI INDICI */}
        <div className="w-full bg-white rounded-xl shadow-md p-6 mb-6 border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">
              {showAllIndices ? '📂 Tutti gli Indici (A–Z)' : '📂 Ultimi Indici (più recenti in cima)'}
            </h2>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowAllIndices(!showAllIndices)}
                className="text-sm text-purple-600 hover:text-purple-700 font-medium"
              >
                {showAllIndices ? '📅 Ordina per data' : '📋 Ordina per nome'}
              </button>
              <button
                onClick={loadIndices}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                🔄 Aggiorna
              </button>
            </div>
          </div>

          {/* 🔄 BANNER INDICIZZAZIONE IN CORSO - DETTAGLIATO */}
          {processingIndex && (
            <div className="mb-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-lg p-4">
              <div className="flex items-start">
                <Loader className="w-6 h-6 text-blue-600 animate-spin mr-3 mt-1 flex-shrink-0" />
                <div className="flex-1">
                  <p className="font-bold text-blue-900 mb-1">⚙️ Indicizzazione in corso...</p>
                  <p className="text-sm text-blue-700 mb-3">
                    Indice: <span className="font-mono font-semibold">{processingIndex}</span>
                    {indexingStartTime && (
                      <span className="ml-3 text-blue-500">
                        (Tempo trascorso: {Math.floor(elapsedTime / 60)}m {elapsedTime % 60}s)
                      </span>
                    )}
                  </p>
                  
                  {!detailedStatus ? (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <p className="text-sm text-yellow-800">
                        🔄 <strong>In attesa di aggiornamenti dal backend...</strong>
                      </p>
                      <p className="text-xs text-yellow-600 mt-1">
                        Il processo è stato avviato. Gli aggiornamenti di progresso arriveranno a breve.
                      </p>
                      {indexingStartTime && (
                        <p className="text-xs text-yellow-700 mt-2 font-mono">
                          ⏱️ Tempo trascorso: {Math.floor(elapsedTime / 60)}m {elapsedTime % 60}s
                        </p>
                      )}
                      <p className="text-xs text-yellow-500 mt-2">
                        💡 Se non vedi aggiornamenti entro 30 secondi, potrebbe esserci un problema. Controlla i log del backend.
                      </p>
                    </div>
                  ) : detailedStatus && (
                    <div className="space-y-2">
                      {/* 🔧 PARAMETRI DI CONFIGURAZIONE */}
                      {detailedStatus.config && (
                        <div className="bg-indigo-50 rounded-lg p-4 border-2 border-indigo-300">
                          <h3 className="text-sm font-bold text-indigo-900 mb-3 flex items-center">
                            <Settings className="w-4 h-4 mr-2" />
                            ⚙️ PARAMETRI DI CONFIGURAZIONE
                          </h3>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div className="bg-white rounded p-2">
                              <span className="text-indigo-600 font-medium">Modello Embedding:</span>
                              <p className="font-mono text-indigo-900 mt-1">{detailedStatus.config.embed_model}</p>
                            </div>
                            <div className="bg-white rounded p-2">
                              <span className="text-indigo-600 font-medium">Extractor:</span>
                              <p className="font-mono text-indigo-900 mt-1">{detailedStatus.config.extractor}</p>
                            </div>
                            <div className="bg-white rounded p-2">
                              <span className="text-indigo-600 font-medium">Chunking:</span>
                              <p className="font-mono text-indigo-900 mt-1">
                                {detailedStatus.config.use_semantic_chunking ? (
                                  <span className="text-purple-700 font-bold">✨ SEMANTICO</span>
                                ) : (
                                  <span className="text-blue-700">📏 Standard</span>
                                )}
                              </p>
                            </div>
                            {!detailedStatus.config.use_semantic_chunking ? (
                              <>
                                <div className="bg-white rounded p-2">
                                  <span className="text-indigo-600 font-medium">Chunk Size:</span>
                                  <p className="font-mono text-indigo-900 mt-1">{detailedStatus.config.chunk_size} caratteri</p>
                                </div>
                                <div className="bg-white rounded p-2">
                                  <span className="text-indigo-600 font-medium">Overlap:</span>
                                  <p className="font-mono text-indigo-900 mt-1">{detailedStatus.config.chunk_overlap} caratteri</p>
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="bg-white rounded p-2">
                                  <span className="text-indigo-600 font-medium">Similarity:</span>
                                  <p className="font-mono text-indigo-900 mt-1">{detailedStatus.config.similarity_threshold}</p>
                                </div>
                                <div className="bg-white rounded p-2">
                                  <span className="text-indigo-600 font-medium">Min/Max chars:</span>
                                  <p className="font-mono text-indigo-900 mt-1">
                                    {detailedStatus.config.min_chunk_chars}-{detailedStatus.config.max_chunk_chars}
                                  </p>
                                </div>
                              </>
                            )}
                            <div className="bg-white rounded p-2">
                              <span className="text-indigo-600 font-medium">OCR:</span>
                              <p className="font-mono text-indigo-900 mt-1">
                                {detailedStatus.config.use_ocr_fallback ? '✅ Attivo' : '❌ Disattivo'}
                              </p>
                            </div>
                            <div className="bg-white rounded p-2">
                              <span className="text-indigo-600 font-medium">Presidio:</span>
                              <p className="font-mono text-indigo-900 mt-1">
                                {detailedStatus.config.use_presidio ? '✅ Attivo' : '❌ Disattivo'}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Barra progresso file */}
                      <div className="bg-white rounded-lg p-3 border border-blue-200">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-blue-800">
                            📄 File {detailedStatus.current_file_index}/{detailedStatus.total_files}
                          </span>
                          <span className="text-xs font-mono text-blue-600">
                            {Math.round((detailedStatus.current_file_index / detailedStatus.total_files) * 100)}%
                          </span>
                        </div>
                        <div className="w-full bg-blue-100 rounded-full h-2">
                          <div 
                            className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${(detailedStatus.current_file_index / detailedStatus.total_files) * 100}%` }}
                          />
                        </div>
                      </div>

                      {/* File corrente */}
                      {detailedStatus.current_file_name && (
                        <div className="bg-white rounded-lg p-3 border border-blue-200">
                          <p className="text-xs font-medium text-blue-700 mb-1">📂 File corrente:</p>
                          <p className="text-sm font-mono text-blue-900 truncate">
                            {detailedStatus.current_file_name}
                          </p>
                        </div>
                      )}

                      {/* Fase corrente */}
                      <div className="bg-white rounded-lg p-3 border border-blue-200">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-blue-700">
                            {detailedStatus.phase === 'extracting_text' && '📝 Estrazione testo...'}
                            {detailedStatus.phase === 'chunking' && '✂️ Chunking in corso...'}
                            {detailedStatus.phase === 'embedding' && '🧮 Generazione embeddings...'}
                            {detailedStatus.phase === 'saving' && '💾 Salvataggio indice...'}
                            {detailedStatus.phase === 'completed' && '✅ Completato!'}
                            {detailedStatus.phase === 'loading_model' && '⏳ Caricamento modello...'}
                            {detailedStatus.phase === 'starting' && '🚀 Avvio...'}
                          </span>
                          {indexingStartTime && (
                            <span className="text-xs font-mono text-blue-500">
                              ⏱️ {Math.floor(elapsedTime / 60)}m {elapsedTime % 60}s
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-blue-600 mt-1">{detailedStatus.message}</p>
                      </div>

                      {/* Chunks info */}
                      {(detailedStatus.file_chunks_count || detailedStatus.total_chunks > 0) && (
                        <div className="bg-white rounded-lg p-3 border border-blue-200">
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            {detailedStatus.file_chunks_count && (
                              <div>
                                <span className="text-blue-600">Chunks file:</span>
                                <span className="ml-2 font-mono font-bold text-blue-900">
                                  {detailedStatus.file_chunks_count}
                                </span>
                              </div>
                            )}
                            {detailedStatus.file_chunks_embedded && (
                              <div>
                                <span className="text-blue-600">Embedded:</span>
                                <span className="ml-2 font-mono font-bold text-green-700">
                                  {detailedStatus.file_chunks_embedded}/{detailedStatus.file_chunks_count}
                                </span>
                              </div>
                            )}
                            {detailedStatus.total_chunks > 0 && (
                              <div className="col-span-2">
                                <span className="text-blue-600">Totale chunks:</span>
                                <span className="ml-2 font-mono font-bold text-blue-900">
                                  {detailedStatus.total_chunks}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* 🆕 ANTEPRIMA CHUNKS quando completato */}
                      {detailedStatus.phase === 'completed' && (detailedStatus as any).chunks_preview && (
                        <div className="bg-green-50 rounded-lg p-4 border-2 border-green-300">
                          <h3 className="text-sm font-bold text-green-900 mb-3">
                            📋 ANTEPRIMA CHUNKS GENERATI
                          </h3>
                          <div className="space-y-3 max-h-96 overflow-y-auto">
                            {(detailedStatus as any).chunks_preview.map((chunk: any, idx: number) => (
                              <div key={idx} className="bg-white rounded-lg p-3 border border-green-200">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-xs font-bold text-green-700">
                                    🔹 CHUNK {chunk.index}/{detailedStatus.total_chunks}
                                  </span>
                                  <span className="text-xs text-green-600">
                                    {chunk.length} caratteri
                                  </span>
                                </div>
                                <div className="text-xs text-gray-600 mb-2 font-mono">
                                  📄 {chunk.source}
                                </div>
                                <div className="bg-gray-50 rounded p-2 text-xs font-mono text-gray-800 whitespace-pre-wrap border border-gray-200">
                                  {chunk.text}
                                  {chunk.length > 500 && <span className="text-gray-400">...</span>}
                                </div>
                              </div>
                            ))}
                          </div>
                          {detailedStatus.total_chunks > 3 && (
                            <p className="text-xs text-green-600 mt-2 text-center">
                              ...e altri {detailedStatus.total_chunks - 3} chunks non visualizzati
                            </p>
                          )}
                        </div>
                      )}
                      
                      <div className="mt-2 pt-2 border-t border-blue-200">
                        <p className="text-xs text-blue-500">
                          💡 L'elaborazione avviene in background. Puoi chiudere questa pagina e tornare dopo.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {indices.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <BookOpen className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg">Nessun indice presente</p>
              <p className="text-sm">Carica un PDF o indicizza un sito web per iniziare</p>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Mostra indici filtrati per data o tutti */}
              {(() => {
                // created_at → ms per ordinamento (Unix sec, stringa numerica, o ISO string)
                const createdAtMs = (v: unknown): number => {
                  if (v == null || v === 'N/A') return 0;
                  if (typeof v === 'number') return v * 1000;
                  if (typeof v === 'string') {
                    if (/^\d+(\.\d+)?$/.test(v)) return parseFloat(v) * 1000;
                    const t = new Date(v.replace(' ', 'T')).getTime();
                    return Number.isNaN(t) ? 0 : t;
                  }
                  return 0;
                };

                // "Ultimi Indici" = ordina per data e mostra solo i primi 3. "Tutti" = ordina per nome, mostra tutti.
                const sortedIndices = showAllIndices
                  ? [...indices].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                  : [...indices].sort((a, b) => {
                      const ma = createdAtMs(a.created_at);
                      const mb = createdAtMs(b.created_at);
                      if (ma === 0 && mb === 0) return (a.name || '').localeCompare(b.name || '');
                      if (ma === 0) return 1;
                      if (mb === 0) return -1;
                      return mb - ma; // più recente prima
                    });
                const indicesToShow = showAllIndices ? sortedIndices : sortedIndices.slice(0, 3);

                return (
                  <>
                    {indicesToShow.map((index) => (
                  <div
                    key={index.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center flex-1">
                      {index.type === 'pdf' ? (
                        <FileText className="w-5 h-5 text-red-500 mr-3" />
                      ) : (
                        <Globe className="w-5 h-5 text-green-500 mr-3" />
                      )}
                      <div>
                        <div className="font-semibold text-gray-900">{index.name}</div>
                        <div className="text-sm text-gray-500">
                          {index.chunks_count.toLocaleString()} chunks • {
                            index.created_at && index.created_at !== 'N/A' 
                              ? new Date(
                                  typeof index.created_at === 'number' 
                                    ? index.created_at * 1000  // Unix timestamp to milliseconds
                                    : index.created_at         // ISO string
                                ).toLocaleString('it-IT', { 
                                  timeZone: 'Europe/Rome',
                                  year: 'numeric',
                                  month: '2-digit',
                                  day: '2-digit',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })
                              : 'N/A'
                          }
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      {index.status === 'processing' ? (
                        <span className="text-xs text-yellow-600 font-medium flex items-center">
                          <Loader className="w-4 h-4 animate-spin mr-1" />
                          Processing...
                        </span>
                      ) : (
                        <span className="text-xs text-green-600 font-medium">✅ Pronto</span>
                      )}
                      
                      <button
                        onClick={() => handleInspectIndex(index.id)}
                        className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                        title="👁️ Visualizza chunks"
                      >
                        <Eye className="w-5 h-5" />
                      </button>
                      
                      <button
                        onClick={() => handleDeleteIndex(index.id)}
                        className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                        title="🗑️ Elimina indice"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}
                    {!showAllIndices && indices.length > 3 && (
                      <p className="text-sm text-gray-500 mt-3 text-center">
                        Mostrando i 3 più recenti. Clicca <strong>Ordina per nome</strong> per vedere tutti gli indici.
                      </p>
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </div>

        {/* 💡 SEZIONE COME USARE */}
        <div className="w-full bg-white rounded-xl shadow-md p-6 mb-6 border border-gray-200">
          <div className="flex items-center mb-4">
            <BookOpen className="w-6 h-6 text-blue-500 mr-2" />
            <h2 className="text-xl font-bold text-gray-900">💡 Come Usare</h2>
          </div>
          <ol className="text-sm text-gray-700 space-y-2 list-decimal list-inside">
            <li>Carica uno o più PDF, oppure indicizza un sito web</li>
            <li>L'indicizzazione avviene in background</li>
            <li>Riceverai una notifica quando l'indice è pronto</li>
            <li>Crea un nodo RAG nel tuo flusso e seleziona l'indice</li>
            <li>Il bot risponderà usando i tuoi documenti!</li>
          </ol>
        </div>

        {/* 🗄️ SEZIONE DATABASE E CLOUD */}
        <div className="w-full bg-white rounded-xl shadow-md p-6 mb-6 border border-gray-200">
          <button
            type="button"
            onClick={() => setShowDatabaseCloudSection(!showDatabaseCloudSection)}
            className="flex items-center justify-between w-full text-left"
          >
            <div className="flex items-center">
              <Database className="w-6 h-6 text-indigo-500 mr-2" />
              <h2 className="text-xl font-bold text-gray-900">🗄️ Database e Cloud</h2>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-red-600 font-semibold text-sm">⚠️ Richiede Piano Custom</span>
              {showDatabaseCloudSection ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </div>
          </button>
          
          <div className={showDatabaseCloudSection ? "mt-4 space-y-4" : "hidden"}>
            {/* Database SQL Connectors */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <DatabaseConnectorPanel />
            </div>

            {/* Indicizzazione Cloud */}
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border-2 border-purple-300 rounded-lg p-4">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
                  <span className="text-xl">☁️</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-md font-bold text-purple-900 mb-2">
                    💡 Indicizzare dati da Cloud
                  </h3>
                  <p className="text-sm text-purple-800 mb-3">
                    Per indicizzare documenti da <strong>Google Drive</strong>, <strong>Gmail</strong>, <strong>Notion</strong>, 
                    <strong> Dropbox</strong>, <strong>Confluence</strong> o <strong>Slack</strong>, 
                    vai alla sezione dedicata:
                  </p>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="px-2 py-1 bg-purple-200 text-purple-900 font-bold rounded text-xs">
                      Sidebar
                    </span>
                    <span className="text-purple-700">→</span>
                    <span className="px-2 py-1 bg-purple-600 text-white font-bold rounded text-xs">
                      🔌 Servizi / API
                    </span>
                    <span className="text-purple-700">→</span>
                    <span className="px-2 py-1 bg-purple-200 text-purple-900 font-bold rounded text-xs">
                      API Connections
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 🔍 MODAL DETTAGLI INDICE (come vanilla UI) */}
        {showInspectModal && inspectData && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowInspectModal(false)}
        >
          <div 
            className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[95vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold">📊 Dettagli Indice</h2>
                  <p className="font-mono text-sm mt-1 text-blue-100">{inspectData.name || 'N/A'}</p>
                </div>
                <button
                  onClick={() => setShowInspectModal(false)}
                  className="text-white hover:text-gray-200 text-3xl font-bold leading-none"
                >
                  ×
                </button>
              </div>
            </div>
            
            {/* Stats */}
            <div className="p-6 bg-gray-50 border-b flex-shrink-0">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-white p-3 rounded-lg shadow-sm">
                  <div className="text-gray-500 text-xs">Chunks Totali</div>
                  <div className="text-xl font-bold text-blue-600">{inspectData.total_chunks?.toLocaleString()}</div>
                </div>
                <div className="bg-white p-3 rounded-lg shadow-sm">
                  <div className="text-gray-500 text-xs">File Totali</div>
                  <div className="text-xl font-bold text-green-600">{inspectData.total_files?.toLocaleString() || 'N/A'}</div>
                </div>
                <div className="bg-white p-3 rounded-lg shadow-sm">
                  <div className="text-gray-500 text-xs">Modello</div>
                  <div className="text-sm font-bold text-purple-600">{inspectData.model || 'N/A'}</div>
                </div>
                <div className="bg-white p-3 rounded-lg shadow-sm">
                  <div className="text-gray-500 text-xs">Dimensione</div>
                  <div className="text-sm font-bold text-orange-600">{inspectData.size ? `${(inspectData.size / 1024 / 1024).toFixed(1)} MB` : 'N/A'}</div>
                </div>
              </div>
            </div>
            
            {/* Paginazione */}
            {inspectData.pagination && (
              <div className="p-4 bg-gray-100 border-b flex items-center justify-between flex-shrink-0">
                <div className="flex gap-2">
                  <button
                    onClick={() => handleInspectIndex(inspectData.id, 1)}
                    disabled={inspectData.pagination.current_page === 1}
                    className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    ⏮️ Prima
                  </button>
                  <button
                    onClick={() => handleInspectIndex(inspectData.id, inspectData.pagination.current_page - 1)}
                    disabled={!inspectData.pagination.has_prev}
                    className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    ⏪ Prec
                  </button>
                </div>
                
                <div className="text-sm text-gray-600">
                  Pagina <strong>{inspectData.pagination.current_page}</strong> di <strong>{inspectData.pagination.total_pages}</strong>
                  {' '}({inspectData.pagination.start_index}-{inspectData.pagination.end_index} di {inspectData.total_chunks})
                </div>
                
                <div className="flex gap-2">
                  <button
                    onClick={() => handleInspectIndex(inspectData.id, inspectData.pagination.current_page + 1)}
                    disabled={!inspectData.pagination.has_next}
                    className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Succ ⏩
                  </button>
                  <button
                    onClick={() => handleInspectIndex(inspectData.id, inspectData.pagination.total_pages)}
                    disabled={inspectData.pagination.current_page === inspectData.pagination.total_pages}
                    className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Ultima ⏭️
                  </button>
                </div>
              </div>
            )}
            
            {/* Chunks List */}
            <div className="flex-1 overflow-y-auto p-6">
              <h3 className="text-lg font-bold mb-4 text-gray-800 sticky top-0 bg-white pb-2">
                📄 Chunks ({inspectData.all_chunks?.length || 0})
              </h3>
              <div className="space-y-3">
                {inspectData.all_chunks?.map((chunk: any, idx: number) => (
                  <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden hover:border-blue-300 transition-colors">
                    <div className="flex items-center justify-between p-3 bg-gray-100 cursor-pointer hover:bg-gray-200">
                      <div className="flex items-center gap-3">
                        <span className="text-gray-400">▶</span>
                        <span className="font-medium text-sm text-gray-700">Chunk {chunk.index || chunk.id}</span>
                        <span className="text-xs text-gray-500">{chunk.content?.substring(0, 80)}...</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                          {chunk.content?.length || 0} char
                        </span>
                        {chunk.source_file && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded truncate max-w-xs">
                            📄 {chunk.source_file}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="p-4 text-sm text-gray-700 whitespace-pre-wrap">
                      {chunk.content || chunk.text || 'Nessun contenuto'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Footer */}
            <div className="p-4 bg-gray-100 flex justify-end gap-2 flex-shrink-0">
              <button
                onClick={() => setShowInspectModal(false)}
                className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white font-bold rounded-lg transition-colors"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

